import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  Avatar,
  Select,
  MenuItem,
  Menu,
  FormControl,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputLabel,
} from '@mui/material';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import { DataGrid } from '@mui/x-data-grid';
import { MoreHoriz, Add, ArrowBackIosNew, ArrowForwardIos, Discount, SyncAlt, ReceiptLong, AddCard, Search, Close, AttachMoney, Store, Edit, Delete } from '@mui/icons-material';
import CustomAutoComplete from './CustomAutoComplete';
import CustomModal from './CustomModal';
import API, { SettlementsAPI } from '../services/api';
import RefreshButtonSpinner from './RefreshButtonSpinner';
import PaymentDocumentForm from '../pages/Payment/CreatePaymentDocument';
import CreditNoteForm from '../pages/Payment/CreateCreditNoteV2';
import SuccessMessage from './SuccessMessage';
import ReceiptForm from '../pages/Payment/CreateReceipt';
import AllocationContent from '../pages/Payment/AllocationContent';
import CustomSnackBar from './CustomSnackBar';
import { useAuth } from '../auth/AuthProvider';
import PaymentSummaryStep from './payment/PaymentSummaryStep';
import PaymentTotalsBar from './payment/PaymentTotalsBar';
import PaymentDetailStep from './payment/PaymentDetailStep';
import {
  BulkReconciliationNotesDialog,
  QuickReconciliationManagerDialog,
  QuickReconciliationNoteDialog,
  QuickReconciliationPanel,
} from './payment/QuickReconciliationNoteDialog';
import { normalizePaymentStatus } from '../utils/paymentStatus';
import { extractApiError } from '../utils/apiError';
import {
  creditAllocationAmount,
  creditRemainingAmount,
} from '../utils/settlementCredits';
import {
  quickNotesForInvoice,
  rebalanceQuickReconciliationNotes,
  reconciliationStateForInvoice,
} from '../utils/quickReconciliation';

const steps = ['Seleccionar facturas', 'Resumen', 'Detalle de pago', 'Finalizado'];

function formatCurrency(v) {
  try {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(v || 0);
  } catch {
    return `Q ${Number(v || 0).toFixed(2)}`;
  }
}

const DATE_LOCALE = 'es-GT';

const todayLocalDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw) return null;

  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const [, y, m, d] = ymd;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = parseDateValue(value);
  if (!date) return '-';
  try {
    return new Intl.DateTimeFormat(DATE_LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return String(value).slice(0, 10);
  }
};

const getSafeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getFirstFiniteNumber = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getFirstNonEmptyText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const roundMoney = (v) => {
  const n = getSafeNumber(v);
  return Math.round(n * 100) / 100;
};

const sumMoney = (values) => roundMoney((values || []).reduce((sum, value) => sum + roundMoney(value), 0));

const moneyDiffAbs = (left, right) => Math.abs(roundMoney(left) - roundMoney(right));

const moneyEquals = (left, right) => moneyDiffAbs(left, right) < 0.01;

const normalizeTextValue = (value) => String(value || '').trim().toLowerCase();

const normalizePaymentMethod = (method, isAch = false) => {
  const raw = String(method || '').toLowerCase();
  if (isAch || raw === 'ach' || raw.includes('transferencia ach')) return 'ach';
  if (raw.includes('transfer')) return 'transfer';
  if (raw.includes('cheque') || raw.includes('check')) return 'check';
  if (raw.includes('efectivo') || raw.includes('cash')) return 'cash';
  if (raw.includes('abasto')) return 'abasto';
  return 'transfer';
};

const getPaymentMethodMeta = (method, isAch = false) => {
  const normalized = normalizePaymentMethod(method, isAch);
  if (normalized === 'ach') {
    return { normalized, label: 'Transferencia ACH', icon: <SyncAlt fontSize='small' />, color: 'info' };
  }
  if (normalized === 'check') {
    return { normalized, label: 'Cheque', icon: <ReceiptLong fontSize='small' />, color: 'warning' };
  }
  if (normalized === 'cash') {
    return { normalized, label: 'Efectivo', icon: <AttachMoney fontSize='small' />, color: 'success' };
  }
  if (normalized === 'abasto') {
    return { normalized, label: 'Abasto', icon: <Store fontSize='small' />, color: 'secondary' };
  }
  return { normalized: 'transfer', label: 'Transferencia', icon: <SyncAlt fontSize='small' />, color: 'primary' };
};

const normalizeClaimStatus = (value) => {
  const raw = String(value || '').toUpperCase();
  return raw === 'RECEIVED_LATER' ? 'DELIVERED_LATER' : raw;
};

const getPendingAdjustmentValue = (inv) => {
  const candidates = [
    inv?.pending_adjustment_amount,
    inv?.pendingAdjustmentAmount,
    inv?.pending_credit_note_amount,
    inv?.pendingCreditNoteAmount,
    inv?.invoice_adjustment_pending,
    inv?.invoiceAdjustmentPending,
    inv?.credit_note_pending_amount,
    inv?.creditNotePendingAmount,
    inv?.invoiceAdjustment,
    inv?.pending_adjustment,
  ];
  for (const val of candidates) {
    const num = Number(val);
    if (Number.isFinite(num)) return Math.max(num, 0);
  }
  return 0;
};

const DEFAULT_SUMMARY_UI_STATE = {
  activeInvoiceId: null,
  detailQuery: '',
  detailStatus: 'all',
  detailPage: 0,
  detailPageSize: 25,
};

const normalizeSummaryUiState = (state = {}, fallbackInvoiceId = null) => ({
  activeInvoiceId:
    state?.activeInvoiceId != null
      ? Number(state.activeInvoiceId)
      : (state?.expandedInvoiceId != null ? Number(state.expandedInvoiceId) : fallbackInvoiceId),
  detailQuery: String(state?.detailQuery || ''),
  detailStatus: String(state?.detailStatus || 'all'),
  detailPage: Number(state?.detailPage || 0),
  detailPageSize: Number(state?.detailPageSize || 25),
});

export default function PaymentStepper({ draft = null, setDraft = null }) {
  const { company } = useAuth();
  const draftSupplier = draft?.supplier || {};
  const draftInvoices = draft?.invoices || {};
  const draftDocuments = draft?.documents || {};
  const draftAdjustments = draft?.adjustments || {};
  const draftMetadata = draft?.metadata || {};

  // Estados principales
  const [activeStep, setActiveStep] = useState(() => Number(draftMetadata.activeStep || 0));
  const [supplierId, setSupplierId] = useState(() => Number(draftSupplier.id || 0));
  const [supplierName, setSupplierName] = useState(() => String(draftSupplier.name || ''));
  const [bankAccounts, setBankAccounts] = useState(() =>
    Array.isArray(draftSupplier.bankAccounts) ? draftSupplier.bankAccounts : []
  );
  const [invoices, setInvoices] = useState(() =>
    Array.isArray(draftInvoices.available) ? draftInvoices.available : []
  );
  const [loading, setLoading] = useState(false);
  const [invoiceScope, setInvoiceScope] = useState(() => String(draftInvoices.scope || 'payable'));
  const [invoiceHasMore, setInvoiceHasMore] = useState(() => Boolean(draftInvoices.hasMore));
  const [invoiceServerOffset, setInvoiceServerOffset] = useState(() => Number(draftInvoices.serverOffset || 0));
  const [invoiceServerLimit] = useState(50);
  const [invoicePaginationModel, setInvoicePaginationModel] = useState(() => {
    const incoming = draftInvoices.paginationModel || {};
    return {
      page: Number(incoming.page || 0),
      pageSize: Number(incoming.pageSize || 10),
    };
  });
  const [selectionModel, setSelectionModel] = useState(() =>
    Array.isArray(draftInvoices.selectedIds) ? draftInvoices.selectedIds : []
  );
  const [openPayDoc, setOpenPayDoc] = useState(false);
  const [openCreditNote, setOpenCreditNote] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
  const [payDocs, setPayDocs] = useState(() =>
    Array.isArray(draftDocuments.paymentDocs) ? draftDocuments.paymentDocs : []
  );
  const [creditNotes, setCreditNotes] = useState(() =>
    rebalanceQuickReconciliationNotes(
      Array.isArray(draftDocuments.creditNotes) ? draftDocuments.creditNotes : [],
      Array.isArray(draftInvoices.available) ? draftInvoices.available : []
    )
  );
  const [receipts, setReceipts] = useState(() =>
    Array.isArray(draftDocuments.receipts) ? draftDocuments.receipts : []
  );
  const [editPayIdx, setEditPayIdx] = useState(-1);
  const [editCreditIdx, setEditCreditIdx] = useState(-1);
  const [editReceiptIdx, setEditReceiptIdx] = useState(-1);
  const [creditNoteContext, setCreditNoteContext] = useState(null);
  const [quickNoteDialog, setQuickNoteDialog] = useState({ open: false, invoiceId: null, editIndex: -1 });
  const [quickManagerInvoiceId, setQuickManagerInvoiceId] = useState(null);
  const [bulkReconciliationOpen, setBulkReconciliationOpen] = useState(false);
  const [supplierEarlyPayments, setSupplierEarlyPayments] = useState(() =>
    Array.isArray(draftSupplier.earlyPayments) ? draftSupplier.earlyPayments : []
  );
  const [earlyPaymentStateByKey, setEarlyPaymentStateByKey] = useState(
    () => draftAdjustments.earlyPaymentStateByKey || {}
  );
  const [earlyPaymentMenuState, setEarlyPaymentMenuState] = useState({ anchorEl: null, suggestionKey: '' });
  const [suggestionSelections, setSuggestionSelections] = useState(
    () => draftAdjustments.suggestionSelections || {}
  );
  const [manualAdjustments, setManualAdjustments] = useState(
    () => draftAdjustments.manualAdjustments || []
  );
  const [manualAdjDraft, setManualAdjDraft] = useState({
    invoiceId: null,
    type: 'shortage',
    amount: '',
    reason: '',
    toBill: false,
    effectiveDate: todayLocalDate(),
  });
  const [bills, setBills] = useState([]);
  const [billUsages, setBillUsages] = useState(() => draftAdjustments.billUsages || {});
  const [loadingBills, setLoadingBills] = useState(false);
  const [detailsByInvoice, setDetailsByInvoice] = useState(() => draftInvoices.detailsByInvoice || {});
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [successInfo, setSuccessInfo] = useState(null);
  const [pendingAchUploads, setPendingAchUploads] = useState([]);
  const [pendingSettlementResult, setPendingSettlementResult] = useState(null);
  const [retryingAchUploads, setRetryingAchUploads] = useState(false);
  const [settlementComment, setSettlementComment] = useState(() => String(draftAdjustments.settlementComment || ''));
  const [allocations, setAllocations] = useState(() => draft?.allocations || {});
  const [openAllocate, setOpenAllocate] = useState(false);
  const [settlementPreview, setSettlementPreview] = useState(null);
  const [minorDifferenceDialog, setMinorDifferenceDialog] = useState({
    open: false,
    amount: 0,
    excessDecision: null,
  });
  const [creditOnlyHasReceipt, setCreditOnlyHasReceipt] = useState(() => Boolean(draftAdjustments.creditOnlyHasReceipt));
  const [excessDialog, setExcessDialog] = useState({
    open: false,
    totalPayable: 0,
    payment: 0,
    excess: 0,
    action: 'ADVANCE',
    invoiceId: '',
  });
  const [summaryUiState, setSummaryUiState] = useState(
    () => normalizeSummaryUiState(draftMetadata.summaryUiState, null)
  );

  // Referencias
  const lastSelectionRef = useRef(Array.isArray(draftInvoices.selectedIds) ? draftInvoices.selectedIds : []);

  useEffect(() => {
    if (typeof setDraft !== 'function') return;
    setDraft((prev) => {
      const current = prev || {};
      return {
        ...current,
        supplier: {
          ...(current.supplier || {}),
          id: supplierId,
          name: supplierName,
          bankAccounts,
          earlyPayments: supplierEarlyPayments,
        },
        invoices: {
          ...(current.invoices || {}),
          available: invoices,
          selectedIds: selectionModel,
          detailsByInvoice,
          scope: invoiceScope,
          hasMore: invoiceHasMore,
          serverOffset: invoiceServerOffset,
          paginationModel: invoicePaginationModel,
        },
        documents: {
          ...(current.documents || {}),
          paymentDocs: payDocs,
          creditNotes,
          receipts,
        },
        allocations,
        adjustments: {
          ...(current.adjustments || {}),
          manualAdjustments,
          suggestionSelections,
          earlyPaymentStateByKey,
          billUsages,
          creditOnlyHasReceipt,
          settlementComment,
        },
        metadata: {
          ...(current.metadata || {}),
          activeStep,
          summaryUiState,
        },
      };
    });
  }, [
    setDraft,
    supplierId,
    supplierName,
    bankAccounts,
    supplierEarlyPayments,
    invoices,
    selectionModel,
    detailsByInvoice,
    invoiceScope,
    invoiceHasMore,
    invoiceServerOffset,
    invoicePaginationModel,
    payDocs,
    creditNotes,
    receipts,
    allocations,
    manualAdjustments,
    suggestionSelections,
    earlyPaymentStateByKey,
    billUsages,
    creditOnlyHasReceipt,
    settlementComment,
    activeStep,
    summaryUiState,
  ]);


  // Callbacks de reinicio
  const resetStepperLight = useCallback(() => {
    setActiveStep(0);
    setInvoices([]);
    setInvoiceHasMore(false);
    setInvoiceServerOffset(0);
    setInvoicePaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectionModel([]);
    setDetailsByInvoice({});
    setPayDocs([]);
    setCreditNotes([]);
    setReceipts([]);
    setEditPayIdx(-1);
    setEditCreditIdx(-1);
    setEditReceiptIdx(-1);
    setCreditNoteContext(null);
    setQuickNoteDialog({ open: false, invoiceId: null, editIndex: -1 });
    setQuickManagerInvoiceId(null);
    setBulkReconciliationOpen(false);
    setAllocations({});
    setSuggestionSelections({});
    setEarlyPaymentStateByKey({});
    setEarlyPaymentMenuState({ anchorEl: null, suggestionKey: '' });
    setManualAdjustments([]);
    setManualAdjDraft({ invoiceId: null, type: 'shortage', amount: '', reason: '', toBill: false, effectiveDate: todayLocalDate() });
    setBills([]);
    setBillUsages({});
    setLoadingBills(false);
    setSuccessInfo(null);
    setPendingAchUploads([]);
    setPendingSettlementResult(null);
    setRetryingAchUploads(false);
    setSettlementComment('');
    setExcessDialog({ open: false, totalPayable: 0, payment: 0, excess: 0, action: 'ADVANCE', invoiceId: '' });
    setSummaryUiState({ ...DEFAULT_SUMMARY_UI_STATE });
    setSnack({ open: false, message: '', severity: 'info' });
    lastSelectionRef.current = [];
  }, []);

  const resetStepper = useCallback(() => {
    setActiveStep(0);
    setSupplierId(0);
    setSupplierName('');
    setBankAccounts([]);
    setSupplierEarlyPayments([]);
    setInvoices([]);
    setInvoiceHasMore(false);
    setInvoiceServerOffset(0);
    setInvoiceScope('payable');
    setInvoicePaginationModel({ page: 0, pageSize: 10 });
    setSelectionModel([]);
    setDetailsByInvoice({});
    setPayDocs([]);
    setCreditNotes([]);
    setReceipts([]);
    setEditPayIdx(-1);
    setEditCreditIdx(-1);
    setEditReceiptIdx(-1);
    setCreditNoteContext(null);
    setQuickNoteDialog({ open: false, invoiceId: null, editIndex: -1 });
    setQuickManagerInvoiceId(null);
    setBulkReconciliationOpen(false);
    setAllocations({});
    setSuggestionSelections({});
    setEarlyPaymentStateByKey({});
    setEarlyPaymentMenuState({ anchorEl: null, suggestionKey: '' });
    setManualAdjustments([]);
    setManualAdjDraft({ invoiceId: null, type: 'shortage', amount: '', reason: '', toBill: false, effectiveDate: todayLocalDate() });
    setBills([]);
    setBillUsages({});
    setLoadingBills(false);
    setSuccessInfo(null);
    setPendingAchUploads([]);
    setPendingSettlementResult(null);
    setRetryingAchUploads(false);
    setSettlementComment('');
    setExcessDialog({ open: false, totalPayable: 0, payment: 0, excess: 0, action: 'ADVANCE', invoiceId: '' });
    setSummaryUiState({ ...DEFAULT_SUMMARY_UI_STATE });
    setSnack({ open: false, message: '', severity: 'info' });
    lastSelectionRef.current = [];
  }, []);

  // Efectos
  useEffect(() => {
    if (!invoices || invoices.length === 0) return;
    const allowed = new Set(invoices.map(r => r.id));
    setSelectionModel(prev => prev.filter(id => allowed.has(id)));
  }, [invoices]);

  const handleSupplierChange = useCallback((name, value) => {
    const newId = value || 0;
    setSupplierId((prev) => {
      if (prev === newId) return prev;
      setSelectionModel([]);
      setInvoices([]);
      setInvoiceHasMore(false);
      setInvoiceServerOffset(0);
      setInvoicePaginationModel((prevPag) => ({ ...prevPag, page: 0 }));
      setDetailsByInvoice({});
      return newId;
    });
  }, []);

  useEffect(() => {
    const fetchSupplier = async () => {
      if (!supplierId) {
        setBankAccounts([]);
        setSupplierEarlyPayments([]);
        return;
      }
      try {
        const res = await API.get(`suppliers/${supplierId}/`);
        setBankAccounts(res.data?.bank_accounts || []);
        setSupplierEarlyPayments((res.data?.early_payments || []).filter((ep) => ep?.is_active !== false));
      } catch (e) {
        console.error('Error cargando proveedor', e);
        setBankAccounts([]);
        setSupplierEarlyPayments([]);
      }
    };
    fetchSupplier();
  }, [supplierId]);

  const fetchInvoices = useCallback(async ({ append = false, offsetOverride = null } = {}) => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const fallbackOffset = Number.isFinite(Number(offsetOverride))
        ? Number(offsetOverride)
        : 0;
      const offset = append ? fallbackOffset : 0;
      const { data } = await SettlementsAPI.eligible(supplierId, {
        scope: invoiceScope,
        limit: invoiceServerLimit,
        offset,
      });
      const isPaginated = !Array.isArray(data) && data && Array.isArray(data.results);
      const rows = isPaginated ? (data.results || []) : (Array.isArray(data) ? data : []);

      const mapped = rows.map((inv) => {
        const priceDiffTotalNum = roundMoney(inv.price_diff_amount || 0);
        const bonusMissingAmountNum = roundMoney(inv.bonus_missing_amount || 0);
        const subtotalBaseNum = roundMoney(inv.subtotal_base ?? inv.total ?? 0);
        const totalEffectiveNum = Number.isFinite(Number(inv.total_effective))
          ? roundMoney(inv.total_effective)
          : subtotalBaseNum;
        const balanceNum = roundMoney(
          (inv.balance != null ? inv.balance : totalEffectiveNum) || 0
        );
        const totalOriginalNum = Number.isFinite(totalEffectiveNum)
          ? totalEffectiveNum
          : 0;
        const abonoNum = roundMoney(Math.max(totalOriginalNum - balanceNum, 0));
        const pendingAdj = roundMoney(getPendingAdjustmentValue(inv));
        const payableSaldoNum = roundMoney(Math.max(0, balanceNum - pendingAdj));
        return {
          id: inv.id,
          number: inv.number,
          series: inv.series,
          date: inv.date || 0,
          createdAt: inv.createdAt || 0,
          invoiceStatus: inv.invoiceStatus,
          paymentStatus: normalizePaymentStatus(inv.paymentStatus),
          totalNum: balanceNum,
          total: formatCurrency(balanceNum),
          totalOriginal: formatCurrency(totalOriginalNum),
          abonoNum,
          saldoNum: balanceNum,
          totalOriginalNum,
          supplier: inv.supplier?.companyName || inv.supplier?.legalName || '',
          pendingAdjustmentAmount: pendingAdj,
          payableSaldoNum,
          pendingAdjustmentOnly: Boolean(inv.pending_adjustment_only),
          hasPendingCreditNote: Boolean(inv.has_pending_credit_note) || pendingAdj > 0,
          hasPriceDiff: Boolean(inv.has_price_diff),
          priceDiffTotalNum,
          hasBonusIssue: Boolean(inv.has_bonus_issue),
          bonusMissingAmountNum,
          appliedAdjustmentTypes: Array.isArray(inv.applied_adjustment_types)
            ? inv.applied_adjustment_types.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        };
      });

      if (isPaginated) {
        setInvoiceHasMore(Boolean(data.has_more));
        const nextOffset = Number(data.next_offset ?? (offset + mapped.length));
        setInvoiceServerOffset(Number.isFinite(nextOffset) ? nextOffset : (offset + mapped.length));
      } else {
        setInvoiceHasMore(false);
        setInvoiceServerOffset(mapped.length);
      }

      setInvoices((prev) => {
        if (!append) return mapped;
        const map = new Map((prev || []).map((row) => [row.id, row]));
        mapped.forEach((row) => map.set(row.id, row));
        return Array.from(map.values());
      });
    } catch (e) {
      console.error('Error obteniendo facturas', e);
      if (!append) setInvoices([]);
      setInvoiceHasMore(false);
      setInvoiceServerOffset(0);
    } finally {
      setLoading(false);
    }
  }, [supplierId, invoiceScope, invoiceServerLimit]);

  useEffect(() => {
    setSelectionModel([]);
    setInvoices([]);
    setInvoiceHasMore(false);
    setInvoiceServerOffset(0);
    setInvoicePaginationModel((prev) => ({ ...prev, page: 0 }));
    if (supplierId) {
      fetchInvoices({ append: false });
    }
  }, [supplierId, invoiceScope, fetchInvoices]);

  const fetchBills = useCallback(async () => {
    if (!supplierId) return;
    setLoadingBills(true);
    try {
      const { data } = await SettlementsAPI.bills(supplierId);
      setBills(data || []);
    } catch (e) {
      console.error('Error cargando saldos a favor', e);
      setBills([]);
    } finally {
      setLoadingBills(false);
    }
  }, [supplierId]);

  useEffect(() => {
    if (activeStep === 0) {
      if ((selectionModel?.length || 0) === 0 && (lastSelectionRef.current?.length || 0) > 0) {
        setSelectionModel(lastSelectionRef.current);
      }
    }
  }, [activeStep, selectionModel?.length]);

  useEffect(() => {
    if (activeStep === 2 && supplierId) {
      fetchBills();
    }
  }, [activeStep, supplierId, fetchBills]);

  useEffect(() => {
    setBillUsages((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([billId, value]) => {
        const bill = (bills || []).find((b) => String(b.id) === String(billId));
        if (!bill) return;
        const available = Number(bill?.saldo_pendiente ?? bill?.remaining_amount ?? bill?.saldo_amount ?? 0);
        if (available <= 0) return;
        const safe = Math.max(0, Math.min(Number(value) || 0, available));
        if (safe > 0) {
          next[billId] = safe;
        }
      });
      return next;
    });
  }, [bills]);

  useEffect(() => {
    const toFetch = selectionModel.filter((id) => !detailsByInvoice[id]);
    if (toFetch.length === 0) return;
    (async () => {
      try {
        const results = await Promise.allSettled(toFetch.map((id) => API.get(`invoice/${id}/`)));
        const updates = {};
        results.forEach((res, idx) => {
          const id = toFetch[idx];
          if (res.status === 'fulfilled') {
            const data = res.value.data || {};
            if (process.env.NODE_ENV !== 'production') {
              console.log('[invoice-detail]', id, {
                bonus_pending: (data.bonus_pending || []).map((b) => b.order_bonus_id),
                invoice_bonus: (data.invoice_bonus || []).map((b) => b.order_bonus_id),
              });
            }
            updates[id] = {
              header: data,
              details: data.invoice_detail || [],
              bonuses: data.invoice_bonus || [],
              bonus_summary_by_product: data.bonus_summary_by_product || {},
              bonus_pending: data.bonus_pending || [],
              pending_bonus_by_product: data.pending_bonus_by_product || {},
            };
          }
        });
        if (Object.keys(updates).length) setDetailsByInvoice((prev) => ({ ...prev, ...updates }));
      } catch (e) {
        // noop
      }
    })();
  }, [selectionModel, detailsByInvoice]);

  // Memos
  const selectedInvoices = useMemo(() => invoices.filter((r) => selectionModel.includes(r.id)), [invoices, selectionModel]);

  const openQuickReconciliationNote = useCallback((invoice, editIndex = -1) => {
    if (!invoice?.id) return;
    setSelectionModel((prev) => (
      prev.includes(invoice.id) ? prev : [...prev, invoice.id]
    ));
    setQuickNoteDialog({
      open: true,
      invoiceId: Number(invoice.id),
      editIndex: Number.isInteger(editIndex) ? editIndex : -1,
    });
  }, []);

  const openQuickReconciliationManager = useCallback((invoice) => {
    if (!invoice?.id) return;
    setSelectionModel((prev) => (
      prev.includes(invoice.id) ? prev : [...prev, invoice.id]
    ));
    setQuickManagerInvoiceId(Number(invoice.id));
  }, []);

  const removeQuickReconciliationNote = useCallback((index) => {
    setCreditNotes((prev) => rebalanceQuickReconciliationNotes(
      prev.filter((_, noteIndex) => noteIndex !== index),
      invoices
    ));
  }, [invoices]);

  const invoiceFilterModel = useMemo(() => ({
    items: [],
    quickFilterValues: invoiceSearch ? [invoiceSearch] : [],
  }), [invoiceSearch]);

  const selectedDetails = useMemo(() => {
    const rows = [];
    selectionModel.forEach((id) => {
      const pack = detailsByInvoice[id];
      if (!pack) return;
      const invNum = pack.header?.number;
      (pack.details || []).forEach((d) => {
        const productObj = d?.product && typeof d.product === 'object' ? d.product : {};
        const orderDetailObj = d?.order_detail && typeof d.order_detail === 'object' ? d.order_detail : {};

        const detailId = d?.id ?? d?.detail_id ?? orderDetailObj?.id ?? null;
        const productId = getFirstFiniteNumber(
          productObj?.id,
          d?.product_id,
          typeof d?.product === 'number' ? d.product : null,
          d?.item_id,
          orderDetailObj?.product_id
        );
        const quantity = getFirstFiniteNumber(
          d?.quantity,
          d?.qty,
          d?.amount,
          d?.ordered_quantity,
          d?.order_quantity,
          d?.quantity_ordered,
          orderDetailObj?.quantity
        );
        const quantityRe = getFirstFiniteNumber(
          d?.quantityRe,
          d?.quantity_re,
          d?.received_quantity,
          d?.receivedQuantity,
          d?.quantity_received,
          d?.qty_received,
          d?.taken_quantity,
          d?.takenQuantity,
          quantity,
          orderDetailObj?.quantityRe
        );
        const invoicePrice = getFirstFiniteNumber(
          d?.price,
          d?.unit_price,
          d?.unitPrice,
          d?.invoice_price,
          d?.invoicePrice,
          d?.line_price,
          d?.unit_cost
        );
        const orderPrice = getFirstFiniteNumber(
          d?.order_price,
          d?.orderPrice,
          d?.oc_price,
          d?.ocPrice,
          orderDetailObj?.finalPrice,
          orderDetailObj?.listPrice
        );
        const subtotal = getFirstFiniteNumber(
          d?.subtotal,
          d?.sub_total,
          d?.line_total,
          d?.total,
          d?.amount_total,
          d?.extended_total,
          d?.price_total,
          quantity != null && invoicePrice != null ? quantity * invoicePrice : null
        );
        const priceDiffRaw = d?.price_diff ?? d?.priceDiff ?? null;
        const priceDiff = getFirstFiniteNumber(
          priceDiffRaw,
          orderPrice != null && invoicePrice != null ? invoicePrice - orderPrice : null,
          0
        );
        rows.push({
          invoiceId: id,
          invoiceNumber: invNum,
          detailId,
          productId: productId ?? null,
          code: getFirstNonEmptyText(productObj?.code, d?.code, d?.product_code, d?.item_code),
          description: getFirstNonEmptyText(productObj?.description, d?.description, d?.product_description, d?.item_description),
          dueDate: getFirstNonEmptyText(d?.dueDate, d?.due_date, d?.date_due),
          quantity: quantity ?? 0,
          quantityRe: quantityRe ?? 0,
          prformat: getFirstNonEmptyText(d?.prformat?.name, d?.prformat_name, d?.prformat?.prformat, typeof d?.prformat === 'string' ? d.prformat : null, d?.format?.name, d?.format_name, orderDetailObj?.prformat?.name),
          price: invoicePrice ?? 0,
          bonusPrice: getFirstFiniteNumber(d?.bonus_price, d?.bonusPrice, d?.price_bonus, orderDetailObj?.bonusPrice),
          orderPrice,
          priceDiffRaw,
          listPrice: getFirstFiniteNumber(d?.listPrice, d?.list_price, d?.priceList, orderDetailObj?.listPrice),
          priceDiff,
          subtotal: subtotal ?? 0,
          orderIds: pack.header?.order_ids || pack.order_ids || [],
        });
      });
    });
    return rows;
  }, [selectionModel, detailsByInvoice]);

  const selectedBonusDetails = useMemo(() => {
    const rows = [];
    selectionModel.forEach((invoiceId) => {
      const pack = detailsByInvoice[invoiceId];
      if (!pack) return;
      const byKey = new Map();
      const mergeBonus = (raw, source) => {
        const orderBonus = raw?.order_bonus && typeof raw.order_bonus === 'object' ? raw.order_bonus : {};
        const orderBonusId = getFirstFiniteNumber(
          raw?.order_bonus_id,
          orderBonus?.id,
          raw?.allocations?.[0]?.order_bonus_id
        );
        const product = raw?.product && typeof raw.product === 'object'
          ? raw.product
          : (orderBonus?.base_product || orderBonus?.product || {});
        const productId = getFirstFiniteNumber(raw?.product_id, product?.id, orderBonus?.base_product_id, orderBonus?.product_id);
        const baseProductId = getFirstFiniteNumber(
          raw?.base_product_id,
          raw?.base_product?.id,
          orderBonus?.base_product_id,
          orderBonus?.base_product?.id
        );
        const key = orderBonusId != null ? `order-bonus:${orderBonusId}` : `product:${productId || 'unknown'}`;
        const previous = byKey.get(key) || {};
        const promised = getFirstFiniteNumber(orderBonus?.quantity, raw?.promised_qty, raw?.quantity, previous.promised, 0) || 0;
        const received = getFirstFiniteNumber(raw?.quantityRe, raw?.received_qty, raw?.quantity_received, previous.received, 0) || 0;
        const pending = getFirstFiniteNumber(raw?.quantity_pending, raw?.pending_units, raw?.missing_qty, previous.pending, Math.max(promised - received, 0)) || 0;
        byKey.set(key, {
          ...previous,
          invoiceId: Number(invoiceId),
          orderBonusId: orderBonusId ?? previous.orderBonusId ?? null,
          productId: productId ?? previous.productId ?? null,
          baseProductId: baseProductId ?? previous.baseProductId ?? null,
          code: getFirstNonEmptyText(raw?.code, product?.code, previous.code),
          description: getFirstNonEmptyText(raw?.description, product?.description, previous.description),
          format: getFirstNonEmptyText(raw?.prformat_name, raw?.prformat?.name, orderBonus?.prformat?.name, previous.format),
          promised: Math.max(promised, previous.promised || 0),
          received: Math.max(received, previous.received || 0),
          pending: Math.max(pending, previous.pending || 0),
          claimStatus: getFirstNonEmptyText(raw?.claim_status, previous.claimStatus, pending > 0 ? 'PENDING' : 'COMPLETE'),
          claimNote: getFirstNonEmptyText(raw?.claim_note, previous.claimNote),
          deliveredLaterInvoice: getFirstNonEmptyText(
            raw?.delivered_later_invoice_number,
            raw?.delivered_later_invoice?.number,
            previous.deliveredLaterInvoice
          ),
          source,
          bonusKind: (
            raw?.is_promotional
            || raw?.is_promo
            || /promo/i.test(String(raw?.type || raw?.reason || ""))
          ) ? 'PROMO' : 'BONUS',
        });
      };
      (pack.bonuses || pack.header?.invoice_bonus || []).forEach((row) => mergeBonus(row, 'invoice_bonus'));
      (pack.bonus_pending || pack.header?.bonus_pending || []).forEach((row) => mergeBonus(row, 'bonus_pending'));
      rows.push(...byKey.values());
    });
    return rows;
  }, [selectionModel, detailsByInvoice]);

  useEffect(() => {
    if (!manualAdjDraft.invoiceId && selectedInvoices.length > 0) {
      setManualAdjDraft((prev) => ({ ...prev, invoiceId: selectedInvoices[0].id }));
    }
  }, [manualAdjDraft.invoiceId, selectedInvoices]);


  const columns = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 70, headerAlign: 'center', align: 'center' },
    {
      field: 'number',
      headerName: 'Numero',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (p) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,              // leve, no grita
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 0.2,
            px: 0.75,
            py: 0.15,
            borderRadius: 0.75,
            bgcolor: 'action.selected',   // sutil
          }}
        >
          #{p.value ?? ''}
        </Typography>
      ),
    },

    { field: 'series', headerName: 'Serie', width: 100, headerAlign: 'center', align: 'center' },
    { field: 'date', headerName: 'Fecha', width: 120, headerAlign: 'center', align: 'center' },
    {
      field: 'paymentStatus',
      headerName: 'Pago',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const paymentMap = {
          PENDING: { label: 'Pendiente', color: 'warning' },
          PARTIAL: { label: 'Parcial', color: 'info' },
          AWAITING_RECONCILIATION: { label: 'Esperando conciliacion', color: 'secondary' },
          PAID: { label: 'Pagada', color: 'success' },
          VOID: { label: 'Anulada', color: 'error' },
        };
        const item = paymentMap[normalizePaymentStatus(params.value, null)];
        return item ? <Chip label={item.label} variant="outlined" color={item.color} /> : '-';
      },
      sortable: false,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_, row) => row?.totalOriginalNum ?? row?.totalNum ?? 0,
      renderCell: (params) => formatCurrency(params?.row?.totalOriginalNum ?? params?.row?.totalNum ?? params?.value ?? 0),
    },
    { field: 'abonoNum', headerName: 'Abono', width: 120, headerAlign: 'center', align: 'center', renderCell: ({ row }) => formatCurrency(row.abonoNum || 0) },
    { field: 'saldoNum', headerName: 'Saldo', width: 120, headerAlign: 'center', align: 'center', renderCell: ({ row }) => formatCurrency(row.saldoNum || 0) },
    {
      field: 'quickReconciliation',
      headerName: 'Conciliar NC',
      width: 330,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const pending = getPendingAdjustmentValue(row);
        if (pending <= 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const state = reconciliationStateForInvoice(row, creditNotes);
        if (state.count === 0) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="warning.main" fontWeight={700}>
                Pendiente {formatCurrency(state.remaining)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  openQuickReconciliationNote(row);
                }}
              >
                Capturar NC
              </Button>
            </Stack>
          );
        }
        if (state.count === 1) {
          const { note, index } = state.notes[0];
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" fontWeight={700}>
                NC #{note.number} · {formatCurrency(note.allocationAmount)}
              </Typography>
              <IconButton
                size="small"
                aria-label="Editar nota de crédito"
                onClick={(event) => {
                  event.stopPropagation();
                  openQuickReconciliationNote(row, index);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                aria-label="Eliminar nota de crédito"
                onClick={(event) => {
                  event.stopPropagation();
                  removeQuickReconciliationNote(index);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          );
        }
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" fontWeight={700}>
              {state.count} NC · {formatCurrency(state.reconciled)} conciliado
            </Typography>
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openQuickReconciliationManager(row);
              }}
            >
              Ver / editar
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'alerts',
      headerName: 'Alertas',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (p) => {
        const r = p.row;
        const alerts = [];

        // Precio/Bono - se muestra directamente
        if (r.hasPriceDiff || r.hasBonusIssue) {
          alerts.push(
            <Box
              key="price-bonus"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                mr: 0.5,
                position: 'relative'
              }}
            >
              <Discount fontSize="small" />
              {/* Indicador visual sin hover */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  border: '1px solid white'
                }}
              />
            </Box>
          );
        }

        // Nota de credito - se muestra directamente
        if (r.hasPendingCreditNote) {
          alerts.push(
            <Box
              key="credit-note"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'info.light',
                color: 'info.contrastText',
                position: 'relative'
              }}
            >
              <ReceiptLong fontSize="small" />
              {/* Indicador visual sin hover */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  border: '1px solid white'
                }}
              />
            </Box>
          );
        }

        // Si no hay alertas, mostramos un indicador neutro
        if (!alerts.length) {
          return (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontStyle: 'italic'
              }}
            >
              Sin alertas
            </Typography>
          );
        }

        return (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            {alerts}
          </Stack>
        );
      }
    }
  ], [
    creditNotes,
    openQuickReconciliationManager,
    openQuickReconciliationNote,
    removeQuickReconciliationNote,
  ]);
  const totalInvoices = useMemo(
    () => roundMoney(selectedInvoices.reduce((s, r) => s + getSafeNumber(r.payableSaldoNum ?? r.totalNum), 0)),
    [selectedInvoices]
  );
  const totalAdjToCompensate = useMemo(() => {
    return roundMoney(selectedInvoices.reduce((s, r) => {
      const saldo = getSafeNumber(r.saldoNum ?? r.totalNum);
      const pending = getSafeNumber(r.pendingAdjustmentAmount);
      return s + Math.min(saldo, pending);
    }, 0));
  }, [selectedInvoices]);
  const hasCompensationOnly = totalInvoices === 0 && totalAdjToCompensate > 0;
  const blockProceed = totalInvoices === 0 && totalAdjToCompensate === 0;
  const reconciliationOnly = selectedInvoices.length > 0 && selectedInvoices.every((invoice) => (
    Boolean(invoice.pendingAdjustmentOnly ?? invoice.pending_adjustment_only)
    || (
      getSafeNumber(invoice.payableSaldoNum ?? invoice.payable_balance) <= 0
      && getPendingAdjustmentValue(invoice) > 0
    )
  ));
  const quickDialogInvoice = useMemo(
    () => invoices.find((invoice) => Number(invoice.id) === Number(quickNoteDialog.invoiceId)) || null,
    [invoices, quickNoteDialog.invoiceId]
  );
  const quickDialogInitialNote = quickNoteDialog.editIndex >= 0
    ? creditNotes[quickNoteDialog.editIndex]
    : null;
  const quickDialogState = useMemo(
    () => reconciliationStateForInvoice(quickDialogInvoice, creditNotes),
    [quickDialogInvoice, creditNotes]
  );
  const quickDialogAvailable = quickNoteDialog.editIndex >= 0
    ? roundMoney(Math.max(
        0,
        quickDialogState.pending - quickDialogState.notes
          .filter(({ index }) => index < quickNoteDialog.editIndex)
          .reduce((total, { note }) => total + Number(note?.allocationAmount || 0), 0)
      ))
    : quickDialogState.remaining;
  const quickManagerInvoice = useMemo(
    () => invoices.find((invoice) => Number(invoice.id) === Number(quickManagerInvoiceId)) || null,
    [invoices, quickManagerInvoiceId]
  );
  const quickManagerNotes = useMemo(
    () => quickNotesForInvoice(creditNotes, quickManagerInvoiceId),
    [creditNotes, quickManagerInvoiceId]
  );
  const quickManagerState = useMemo(
    () => reconciliationStateForInvoice(quickManagerInvoice, creditNotes),
    [quickManagerInvoice, creditNotes]
  );
  const promoByInvoice = useMemo(() => {
    const invoiceMeta = new Map();
    selectedInvoices.forEach((inv) => {
      const pack = detailsByInvoice[inv.id];
      const header = pack?.header || {};
      const rawDate = header.date || header.createdAt || inv.date || inv.createdAt;
      const parsed = rawDate ? Date.parse(rawDate) : NaN;
      const sortKey = Number.isFinite(parsed) ? parsed : Number(inv.id || 0);
      invoiceMeta.set(inv.id, {
        number: inv.number ?? header.number,
        series: inv.series ?? header.series,
        sortKey,
      });
    });

    const list = [];

    selectedInvoices.forEach((inv) => {
      const invId = inv.id;
      const pack = detailsByInvoice[invId];
      if (!pack) return;

      const header = pack.header || {};
      const bonuses = pack.bonuses || header.invoice_bonus || [];
      const pendingRows = pack.bonus_pending || header.bonus_pending || [];
      const hasPromo = bonuses.length > 0 || pendingRows.length > 0;
      if (!hasPromo) return;

      let claimPending = 0;
      let claimAdjusted = 0;
      let claimResolved = 0;
      let claimTotal = 0;
      let hasPending = false;
      const notes = new Set();
      const seen = new Set();
      pendingRows.forEach((row, idx) => {
        const key = row.order_bonus_id != null ? `ob:${row.order_bonus_id}` : `idx:${idx}`;
        if (seen.has(key)) return;
        seen.add(key);

        const status = normalizeClaimStatus(row.claim_status);
        const pending = getSafeNumber(row.claim_pending_amount);
        const adjusted = getSafeNumber(row.claim_adjusted_amount);
        const resolved = getSafeNumber(row.claim_resolved_amount ?? row.claim_delivered_later_amount);
        const total = getSafeNumber(row.claim_total_amount ?? (pending + adjusted + resolved));
        if (status === 'PENDING') {
          const pendingUnits = getSafeNumber(row.missing_qty ?? row.quantity_pending ?? row.pending_units);
          if (pending > 0 || pendingUnits > 0) hasPending = true;
          claimPending += pending;
        }
        claimAdjusted += adjusted;
        if (status === 'DELIVERED_LATER') claimResolved += resolved;
        claimTotal += total;
        if (row.claim_note) notes.add(String(row.claim_note));
      });
      const meta = invoiceMeta.get(invId) || {};
      list.push({
        invoiceId: invId,
        number: meta.number ?? inv.number ?? header.number,
        series: meta.series ?? inv.series ?? header.series,
        claimTotal,
        claimPending,
        hasPending,
        claimedByAdjustments: claimAdjusted,
        deliveredLaterAmount: claimResolved,
        deliveredLaterInvoices: [],
        deliveredLaterLabels: Array.from(notes),
        hasPromo,
      });
    });

    return list;
  }, [selectedInvoices, detailsByInvoice]);


  const promoSummary = useMemo(() => {
    const hasPromo = promoByInvoice.some((p) => p.hasPromo);
    const hasPending = promoByInvoice.some((p) => p.hasPending);
    return { hasPromo, hasPending };
  }, [promoByInvoice]);

  const showNotCompleted = promoSummary.hasPending;
  const showCompleteNeutral = promoSummary.hasPromo && !promoSummary.hasPending;

  const totalPayments = useMemo(() => sumMoney(payDocs.map((r) => r.amount || 0)), [payDocs]);
  const totalCredits = useMemo(
    () => sumMoney(creditNotes.map((note) => creditAllocationAmount(note))),
    [creditNotes]
  );
  const totalPreviousPayments = useMemo(
    () => sumMoney(selectedInvoices.map((inv) => inv.abonoNum || 0)),
    [selectedInvoices]
  );
  const isCashMethod = useCallback((method) => {
    const v = String(method || '').toLowerCase().trim();
    return v === 'cash' || v === 'efectivo' || v.includes('efectivo');
  }, []);
  const hasNonCashPayments = useMemo(
    () => payDocs.some((p) => !isCashMethod(p?.type)),
    [payDocs, isCashMethod]
  );
  const cashOnlyPayments = useMemo(
    () => payDocs.length > 0 && !hasNonCashPayments,
    [payDocs.length, hasNonCashPayments]
  );

  const invoiceLookup = useMemo(
    () => Object.fromEntries(selectedInvoices.map((inv) => [Number(inv.id), inv])),
    [selectedInvoices]
  );
  const invoiceAppliedTypeMap = useMemo(
    () =>
      Object.fromEntries(
        selectedInvoices.map((inv) => [
          Number(inv.id),
          new Set((inv?.appliedAdjustmentTypes || []).map((item) => String(item || '').trim())),
        ])
      ),
    [selectedInvoices]
  );

  const invoiceMinDueDateMap = useMemo(() => {
    const map = {};
    selectedInvoices.forEach((inv) => {
      const rows = detailsByInvoice[inv.id]?.details || [];
      const dates = rows
        .map((row) => parseDateValue(row?.dueDate))
        .filter(Boolean)
        .sort((left, right) => left.getTime() - right.getTime());
      map[inv.id] = dates[0] || parseDateValue(inv.date) || null;
    });
    return map;
  }, [selectedInvoices, detailsByInvoice]);

  const shortageSuggestions = useMemo(() => {
    const groups = new Map();

    selectedDetails
      .filter((row) => Number(row.quantityRe) < Number(row.quantity))
      .forEach((row) => {
        const invoiceId = Number(row.invoiceId);
        const missingQty = Math.max(Number(row.quantity || 0) - Number(row.quantityRe || 0), 0);
        const amount = roundMoney(missingQty * Number(row.price || 0));
        if (!invoiceId || amount <= 0) return;

        const key = `shortage:${invoiceId}`;
        const current = groups.get(key) || {
          key,
          invoiceId,
          type: 'shortage',
          amount: 0,
          missingQty: 0,
          lines: 0,
          descriptions: [],
          items: [],
          defaultEnabled: true,
          allowBill: true,
        };

        current.amount = roundMoney(current.amount + amount);
        current.missingQty += missingQty;
        current.lines += 1;
        if (row.description && !current.descriptions.includes(row.description)) {
          current.descriptions.push(row.description);
        }
        current.items.push({
          detailId: row.detailId || null,
          description: row.description || 'Producto',
          amount,
          missingQty,
        });
        groups.set(key, current);
      });

    return Array.from(groups.values()).map((group) => {
      const appliedTypes = invoiceAppliedTypeMap[group.invoiceId];
      if (appliedTypes?.has('shortage') || appliedTypes?.has('product_shortage')) {
        return null;
      }
      const inv = invoiceLookup[group.invoiceId] || {};
      const title = `Factura #${inv.number || group.invoiceId}${inv.series ? ` - ${inv.series}` : ''}`;
      const reason =
        group.lines === 1
          ? `Faltante detectado: ${group.descriptions[0] || 'producto'}`
          : `Faltantes detectados en ${group.lines} productos`;

      return {
        ...group,
        title,
        label: 'Faltantes detectados',
        amount: roundMoney(group.amount),
        reason,
        helper:
          group.lines === 1
            ? `${group.missingQty} unidades faltantes`
            : `${group.missingQty} unidades faltantes en total`,
      };
    }).filter(Boolean);
  }, [selectedDetails, invoiceLookup, invoiceAppliedTypeMap]);

  const priceDiffSuggestions = useMemo(
    () =>
      selectedInvoices
        .filter((inv) => !invoiceAppliedTypeMap[Number(inv.id)]?.has('price_diff'))
        .filter((inv) => getSafeNumber(inv.priceDiffTotalNum) > 0)
        .map((inv) => ({
          key: `price_diff:${inv.id}`,
          invoiceId: inv.id,
          type: 'price_diff',
          amount: roundMoney(inv.priceDiffTotalNum),
          defaultEnabled: false,
          allowBill: false,
          title: `Factura #${inv.number}${inv.series ? ` - ${inv.series}` : ''}`,
          label: 'Diferencia de precio sugerida',
          helper: 'No se aplica por defecto. Activala solo si deseas descontarla ahora.',
          reason: 'Diferencia de precio detectada en liquidacion',
        })),
    [selectedInvoices, invoiceAppliedTypeMap]
  );

  const bonusSuggestions = useMemo(
    () =>
      selectedInvoices
        .filter((inv) => !invoiceAppliedTypeMap[Number(inv.id)]?.has('promo_bonus'))
        .filter((inv) => getSafeNumber(inv.bonusMissingAmountNum) > 0)
        .map((inv) => ({
          key: `promo_bonus:${inv.id}`,
          invoiceId: inv.id,
          type: 'promo_bonus',
          amount: roundMoney(inv.bonusMissingAmountNum),
          defaultEnabled: false,
          allowBill: false,
          title: `Factura #${inv.number}${inv.series ? ` - ${inv.series}` : ''}`,
          label: 'Bonificacion sugerida',
          helper: 'No se aplica por defecto. Activala si la bonificacion debe descontarse en esta liquidacion.',
          reason: 'Bonificacion pendiente detectada en liquidacion',
        })),
    [selectedInvoices, invoiceAppliedTypeMap]
  );

  const earlyPaymentOptionsByInvoice = useMemo(() => {
    const activeRules = (supplierEarlyPayments || []).filter((ep) => ep?.is_active !== false);
    if (!activeRules.length) return {};

    const today = parseDateValue(new Date());
    if (!today) return {};
    today.setHours(0, 0, 0, 0);

    const result = {};

    selectedInvoices.forEach((inv) => {
      if (invoiceAppliedTypeMap[Number(inv.id)]?.has('pronto_pago')) {
        return;
      }
      const options = activeRules
        .map((ep) => {
          const baseDate = ep?.dueDateInvoice ? invoiceMinDueDateMap[inv.id] : parseDateValue(inv.date);
          if (!baseDate) return null;
          const normalizedBase = new Date(baseDate);
          normalizedBase.setHours(0, 0, 0, 0);

          const period = Number(ep?.datePeriod || 0);
          const percent = getSafeNumber(ep?.percent);
          if (period <= 0 || percent <= 0) return null;

          const daysSince = Math.floor((today.getTime() - normalizedBase.getTime()) / 86400000);
          if (daysSince < 0 || daysSince > period) return null;

          return {
            ep,
            optionKey: `${ep?.id ?? 'ep'}:${period}:${percent}:${ep?.dueDateInvoice ? 1 : 0}`,
            dueIn: period - daysSince,
            period,
            percent,
            baseType: ep?.dueDateInvoice ? 'vencimiento' : 'factura',
          };
        })
        .filter(Boolean)
        .sort((left, right) => getSafeNumber(right.percent) - getSafeNumber(left.percent));

      if (!options.length) return;
      result[inv.id] = options;
    });

    return result;
  }, [selectedInvoices, supplierEarlyPayments, invoiceMinDueDateMap, invoiceAppliedTypeMap]);

  const earlyPaymentBaseByKey = useMemo(() => {
    const result = {};
    selectedInvoices.forEach((inv) => {
      const invoiceId = Number(inv.id);
      const options = earlyPaymentOptionsByInvoice[invoiceId] || [];
      if (!options.length) return;
      const baseAmount = roundMoney(inv.payableSaldoNum ?? inv.totalNum);
      const suggestionKey = `pronto_pago:${invoiceId}`;
      result[suggestionKey] = {
        key: suggestionKey,
        invoiceId,
        options,
        baseAmount,
        title: `Factura #${inv.number}${inv.series ? ` - ${inv.series}` : ''}`,
        label: 'Pronto pago sugerido',
        reason: 'Sugerencia de pronto pago',
      };
    });
    return result;
  }, [selectedInvoices, earlyPaymentOptionsByInvoice]);

  useEffect(() => {
    setEarlyPaymentStateByKey((prev) => {
      const next = {};
      Object.values(earlyPaymentBaseByKey).forEach((base) => {
        const current = prev?.[base.key] || {};
        const options = Array.isArray(base.options) ? base.options : [];
        if (!options.length) return;

        const currentOptionKey = String(current.selectedOptionKey || '').trim();
        const selectedOption = options.find((opt) => opt.optionKey === currentOptionKey) || options[0];
        const selectedOptionKey = String(selectedOption.optionKey || '').trim();
        const suggestedAmount = roundMoney(base.baseAmount * (getSafeNumber(selectedOption.percent) / 100));
        const optionChanged = currentOptionKey && currentOptionKey !== selectedOptionKey;
        const hasManualAmount = current.amountEditable !== undefined && current.amountEditable !== null && current.amountEditable !== '';
        const manualEdited = Boolean(current.amountManuallyEdited) && !optionChanged;
        const amountEditable = manualEdited && hasManualAmount
          ? roundMoney(current.amountEditable)
          : suggestedAmount;
        const hasLinkedNote = Boolean((creditNotes || []).find((note) => note?.linkedSuggestionKey === base.key));

        next[base.key] = {
          key: base.key,
          invoiceId: base.invoiceId,
          selectedOptionKey,
          suggestedAmount,
          amountEditable,
          amountManuallyEdited: manualEdited && hasManualAmount,
          enabled: hasLinkedNote ? false : (typeof current.enabled === 'boolean' ? current.enabled : false),
          toBill: false,
        };
      });
      return next;
    });
  }, [earlyPaymentBaseByKey, creditNotes]);

  const earlyPaymentSuggestions = useMemo(() => (
    selectedInvoices
      .map((inv) => {
        const suggestionKey = `pronto_pago:${Number(inv.id)}`;
        const base = earlyPaymentBaseByKey[suggestionKey];
        const state = earlyPaymentStateByKey[suggestionKey];
        if (!base || !state) return null;
        const options = base.options || [];
        if (!options.length) return null;
        const selectedOption = options.find((opt) => opt.optionKey === state.selectedOptionKey) || options[0];
        const amount = roundMoney(state.amountEditable ?? state.suggestedAmount ?? 0);
        if (amount <= 0) return null;
        const percent = getSafeNumber(selectedOption?.percent);
        const period = getSafeNumber(selectedOption?.period);
        const dueIn = getSafeNumber(selectedOption?.dueIn);
        return {
          key: suggestionKey,
          invoiceId: Number(inv.id),
          type: 'pronto_pago',
          amount,
          defaultEnabled: false,
          allowBill: false,
          editableAmount: true,
          percent,
          period,
          dueIn,
          ruleOptionKey: state.selectedOptionKey,
          optionCount: options.length,
          title: base.title,
          label: base.label,
          helper: `Sugerido ${percent}%  ${period} dia(s) plazo  ${dueIn} dia(s) restantes`,
          reason: `${base.reason} ${percent}%`,
        };
      })
      .filter(Boolean)
  ), [selectedInvoices, earlyPaymentBaseByKey, earlyPaymentStateByKey]);

  const nonEarlyAdjustmentSuggestions = useMemo(
    () => [...shortageSuggestions, ...priceDiffSuggestions, ...bonusSuggestions],
    [shortageSuggestions, priceDiffSuggestions, bonusSuggestions]
  );

  useEffect(() => {
    setSuggestionSelections((prev) => {
      const next = {};
      nonEarlyAdjustmentSuggestions.forEach((suggestion) => {
        const current = prev?.[suggestion.key] || {};
        const linkedNote = (creditNotes || []).find((note) => note?.linkedSuggestionKey === suggestion.key);
        next[suggestion.key] = {
          enabled: linkedNote ? false : (typeof current.enabled === 'boolean' ? current.enabled : Boolean(suggestion.defaultEnabled)),
          toBill: linkedNote ? false : (suggestion.allowBill ? Boolean(current.toBill) : false),
          amount: roundMoney(current.amount ?? suggestion.amount ?? 0),
        };
      });
      return next;
    });
  }, [nonEarlyAdjustmentSuggestions, creditNotes]);

  useEffect(() => {
    if (!earlyPaymentMenuState.suggestionKey) return;
    if (earlyPaymentBaseByKey[earlyPaymentMenuState.suggestionKey]) return;
    setEarlyPaymentMenuState({ anchorEl: null, suggestionKey: '' });
  }, [earlyPaymentMenuState.suggestionKey, earlyPaymentBaseByKey]);

  const linkedSuggestionNoteMap = useMemo(() => (
    Object.fromEntries(
      (creditNotes || [])
        .filter((note) => note?.linkedSuggestionKey)
        .map((note) => [note.linkedSuggestionKey, note])
    )
  ), [creditNotes]);

  const acceptedAutoAdjustments = useMemo(() => {
    const nonEarlyAccepted = nonEarlyAdjustmentSuggestions.flatMap((suggestion) => {
      if (linkedSuggestionNoteMap[suggestion.key]) return [];
      const selection = suggestionSelections?.[suggestion.key];
      if (!selection?.enabled) return [];
      const amount = roundMoney(selection.amount ?? suggestion.amount);
      if (amount <= 0) return [];
      return [{
        invoice_id: suggestion.invoiceId,
        detail_id: null,
        type: suggestion.type,
        amount,
        effective_date: todayLocalDate(),
        reason: suggestion.reason || suggestion.label || '',
        to_bill: Boolean(selection.toBill && suggestion.allowBill),
        source: 'suggestion',
        group_key: suggestion.key,
      }];
    });

    const earlyAccepted = earlyPaymentSuggestions.flatMap((suggestion) => {
      if (linkedSuggestionNoteMap[suggestion.key]) return [];
      const state = earlyPaymentStateByKey?.[suggestion.key];
      if (!state?.enabled) return [];
      const amount = roundMoney(state.amountEditable ?? suggestion.amount);
      if (amount <= 0) return [];
      return [{
        invoice_id: suggestion.invoiceId,
        detail_id: null,
        type: suggestion.type,
        amount,
        effective_date: todayLocalDate(),
        reason: suggestion.reason || suggestion.label || '',
        to_bill: false,
        source: 'suggestion',
        group_key: suggestion.key,
      }];
    });

    return [...nonEarlyAccepted, ...earlyAccepted];
  }, [
    nonEarlyAdjustmentSuggestions,
    suggestionSelections,
    linkedSuggestionNoteMap,
    earlyPaymentSuggestions,
    earlyPaymentStateByKey,
  ]);

  const manualAdjustmentsPayload = useMemo(() => manualAdjustments.map((m, idx) => ({
    invoice_id: m.invoiceId,
    detail_id: m.detailId || null,
    type: m.type,
    amount: Number(m.amount || 0),
    effective_date: m.effectiveDate || todayLocalDate(),
    reason: m.reason || '',
    to_bill: Boolean(m.toBill),
    source: 'manual',
    group_key: m.groupKey || `manual:${m.invoiceId}:${m.type}:${idx}`,
  })), [manualAdjustments]);

  const quickReconciliationInvoiceIds = useMemo(() => new Set(
    creditNotes
      .filter((note) => (
        String(note?.applicationMode || '').toLowerCase() === 'reconciliation'
        && !String(note?.linkedType || '').trim()
      ))
      .map((note) => Number(note.invoiceId))
      .filter(Boolean)
  ), [creditNotes]);

  const adjustmentsPayload = useMemo(
    () => [...acceptedAutoAdjustments, ...manualAdjustmentsPayload].filter(
      (adjustment) => !quickReconciliationInvoiceIds.has(Number(adjustment.invoice_id))
    ),
    [acceptedAutoAdjustments, manualAdjustmentsPayload, quickReconciliationInvoiceIds]
  );

  const adjustmentsTotal = useMemo(() => sumMoney(adjustmentsPayload.map((a) => a.amount || 0)), [adjustmentsPayload]);
  const optionalAdjustmentSuggestions = useMemo(
    () => [...priceDiffSuggestions, ...bonusSuggestions, ...earlyPaymentSuggestions],
    [priceDiffSuggestions, bonusSuggestions, earlyPaymentSuggestions]
  );
  const selectableBills = useMemo(
    () => (bills || []).filter((b) => roundMoney(b?.saldo_pendiente ?? b?.remaining_amount ?? b?.saldo_amount ?? 0) > 0),
    [bills]
  );
  const billCreditsTotal = useMemo(() => sumMoney(Object.values(billUsages || {})), [billUsages]);
  const invoiceNetDueBeforeBills = useMemo(() => {
    const map = {};

    selectedInvoices.forEach((inv) => {
      map[inv.id] = roundMoney(inv.payableSaldoNum ?? inv.totalNum);
    });

    adjustmentsPayload.forEach((adj) => {
      const invoiceId = Number(adj?.invoice_id);
      if (!invoiceId || map[invoiceId] == null) return;
      map[invoiceId] = roundMoney(Math.max(0, map[invoiceId] - Number(adj.amount || 0)));
    });

    creditNotes.forEach((note) => {
      const invoiceId = Number(note?.invoiceId);
      if (!invoiceId || map[invoiceId] == null) return;
      map[invoiceId] = roundMoney(
        Math.max(0, map[invoiceId] - creditAllocationAmount(note))
      );
    });

    return map;
  }, [selectedInvoices, adjustmentsPayload, creditNotes]);

  const invoiceNetDueMap = useMemo(() => {
    const map = { ...invoiceNetDueBeforeBills };
    let remainingBill = roundMoney(billCreditsTotal);

    selectedInvoices.forEach((inv) => {
      const invoiceId = Number(inv.id);
      if (remainingBill <= 0 || map[invoiceId] == null) return;
      const take = roundMoney(Math.min(map[invoiceId], remainingBill));
      map[invoiceId] = roundMoney(Math.max(0, map[invoiceId] - take));
      remainingBill = roundMoney(Math.max(0, remainingBill - take));
    });

    return map;
  }, [invoiceNetDueBeforeBills, selectedInvoices, billCreditsTotal]);

  const effectiveDue = useMemo(
    () => roundMoney(Object.values(invoiceNetDueMap).reduce((sum, value) => sum + Number(value || 0), 0)),
    [invoiceNetDueMap]
  );
  const remaining = useMemo(() => roundMoney(effectiveDue - totalPayments), [effectiveDue, totalPayments]);
  const payableByInvoice = invoiceNetDueMap;

  const SEP = ' | ';
  const movements = useMemo(() => {
    const pay = payDocs.map((d, idx) => {
      const methodMeta = getPaymentMethodMeta(d.type, d.isAch);
      const acc = d.bankAccount ? `${d.bankAccount.bankName || ''} ${d.bankAccount.accNumber || ''}`.trim() : '';
      return {
        key: `pay-${idx}`,
        type: 'Pago',
        color: methodMeta.color,
        icon: methodMeta.icon,
        title: `${methodMeta.label}${d.reference ? ` #${d.reference}` : ''}`,
        subtitle: `${formatDate(d.date)}${acc ? `${SEP}${acc}` : ''}`,
        amount: Number(d.amount || 0),
        onEdit: () => { setEditPayIdx(idx); setOpenPayDoc(true); },
        onDelete: () => handleRemovePayDoc(idx),
      };
    });
    const notes = creditNotes.map((c, idx) => ({
      key: `note-${idx}`,
      type: 'Nota',
      color: 'warning',
      icon: <Discount fontSize='small' />,
      title: `Nota${c.number ? ` #${c.number}` : ''}`,
      subtitle: (() => {
        const invoice = invoiceLookup[Number(c.invoiceId)];
        const invoicePart = c.linkedInvoiceLabel
          || (invoice ? `Factura #${invoice.number}${invoice.series ? `  ${invoice.series}` : ''}` : '');
        const linkedPart = c.linkedSuggestionLabel || '';
        const modePart = c.forAdjustmentsOnly ? 'Compensacion de ajustes' : 'aplicacion directa';
        return [
          formatDate(c.date),
          invoicePart,
          linkedPart,
          modePart,
          `Monto de la nota: ${formatCurrency(c.amount || 0)}`,
          `Monto que conciliara: ${formatCurrency(creditAllocationAmount(c))}`,
          `Saldo restante: ${formatCurrency(creditRemainingAmount(c))}`,
          c.reason || '',
        ].filter(Boolean).join(SEP);
      })(),
      amount: Number(c.amount || 0),
      onEdit: () => {
        setEditCreditIdx(idx);
        setCreditNoteContext(
          c?.linkedSuggestionKey
            ? {
                mode: 'linked-suggestion',
                suggestionKey: c.linkedSuggestionKey,
                linkedType: c.linkedType,
                linkedSuggestionLabel: c.linkedSuggestionLabel,
                linkedInvoiceLabel: c.linkedInvoiceLabel,
                invoiceId: c.invoiceId,
                amount: c.amount,
                reason: c.reason,
                forAdjustmentsOnly: Boolean(c.forAdjustmentsOnly),
                linkedEffectiveDate: c.linkedEffectiveDate || todayLocalDate(),
                linkedAdjustmentAmount: c.linkedAdjustmentAmount ?? c.amount,
                linkedDetailId: c.linkedDetailId || null,
                existingNote: c,
              }
            : null
        );
        setOpenCreditNote(true);
      },
      onDelete: () => handleRemoveCredit(idx),
    }));
    const recs = receipts.map((r, idx) => ({
      key: `rec-${idx}`,
      type: 'Recibo',
      color: 'info',
      icon: <ReceiptLong fontSize='small' />,
      title: `Recibo${r.number ? ` #${r.number}` : ''}`,
      subtitle: `${formatDate(r.date)}${r.rType ? `${SEP}${r.rType}` : ''}${r.is_ph ? `${SEP}PH` : ''}`,
      amount: Number(r.amount || 0),
      onEdit: () => { setEditReceiptIdx(idx); setOpenReceipt(true); },
      onDelete: () => handleRemoveReceipt(idx),
    }));
    return [...pay, ...notes, ...recs];
  }, [payDocs, creditNotes, receipts, invoiceLookup]);

  const canDistribute2 = Boolean(
    !blockProceed
    && !hasCompensationOnly
    && settlementPreview?.requires_distribution
  );

  const distributionComplete2 = Boolean(
    canDistribute2 && settlementPreview?.distribution_complete
  );

  const finalizeGuard2 = useMemo(() => {
    if (blockProceed) return { ok: false, reason: 'No hay saldo pagable para las facturas seleccionadas.' };
    if (reconciliationOnly) {
      if (payDocs.length > 0) {
        return { ok: false, reason: 'No se permiten pagos monetarios en la conciliación de ajustes.' };
      }
      const pendingInvoice = selectedInvoices.find(
        (invoice) => reconciliationStateForInvoice(invoice, creditNotes).remaining > 0
      );
      if (pendingInvoice) {
        return {
          ok: false,
          reason: `La factura #${pendingInvoice.number || pendingInvoice.id} aún tiene ajustes pendientes de conciliar.`,
        };
      }
      return { ok: true, reason: '' };
    }
    if (hasCompensationOnly) {
      if (payDocs.length > 0) return { ok: false, reason: 'No se permiten pagos en efectivo para la compensacion.' };
      if (creditNotes.length === 0 && billCreditsTotal <= 0) return { ok: true, reason: '' };
      if (creditNotes.some(c => !c.invoiceId))
        return { ok: false, reason: 'Asigna cada nota de credito a una factura' };
      return { ok: true, reason: '' };
    }
    if (payDocs.length === 0 && creditNotes.length === 0 && billCreditsTotal <= 0) return { ok: false, reason: 'Agrega al menos un pago, nota de credito o saldo a favor' };
    if (creditNotes.some(c => !c.invoiceId))
      return { ok: false, reason: 'Asigna cada nota de credito a una factura' };
    return { ok: true, reason: '' };
  }, [
    blockProceed,
    reconciliationOnly,
    selectedInvoices,
    hasCompensationOnly,
    payDocs.length,
    billCreditsTotal,
    creditNotes,
  ]);

  useEffect(() => {
    setAllocations((prev) => {
      const next = {};
      let remainingToAssign = roundMoney(totalPayments);
      let changed = false;

      selectedInvoices.forEach((inv) => {
        const invoiceId = Number(inv.id);
        const current = roundMoney(prev?.[invoiceId] || 0);
        const maxForInvoice = roundMoney(payableByInvoice[invoiceId] || 0);
        const clamped = roundMoney(Math.max(0, Math.min(current, maxForInvoice, remainingToAssign)));
        if (clamped > 0) {
          next[invoiceId] = clamped;
          remainingToAssign = roundMoney(Math.max(0, remainingToAssign - clamped));
        }
        if (!moneyEquals(current, clamped)) {
          changed = true;
        }
      });

      Object.keys(prev || {}).forEach((invoiceId) => {
        if (!(Number(invoiceId) in next) && Number(prev[invoiceId] || 0) > 0) {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [selectedInvoices, payableByInvoice, totalPayments]);

  // Handlers
  const handleAddPayDoc = (doc) => {
    if (blockProceed) {
      setSnack({ open: true, message: 'No hay saldo pagable para agregar pagos.', severity: 'warning' });
      return;
    }
    if (hasCompensationOnly) {
      setSnack({ open: true, message: 'Estas facturas se liquidaran por compensacion. No se requiere pago en efectivo.', severity: 'warning' });
      return;
    }
    if (editPayIdx >= 0) {
      setPayDocs((arr) => arr.map((item, i) => (i === editPayIdx ? doc : item)));
      setEditPayIdx(-1);
    } else {
      setPayDocs((arr) => [...arr, doc]);
    }
  };

  const handleRemovePayDoc = (idx) => setPayDocs((arr) => arr.filter((_, i) => i !== idx));

  const validateQuickCreditNumbers = async (incomingNotes, excludedIndexes = []) => {
    const excluded = new Set(excludedIndexes);
    const existing = new Set(
      creditNotes
        .filter((_, index) => !excluded.has(index))
        .map((note) => String(note?.number || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const incoming = (incomingNotes || []).map((note) => String(note?.number || '').trim());
    const normalized = incoming.map((number) => number.toLowerCase());
    if (
      normalized.some((number) => !number || existing.has(number))
      || new Set(normalized).size !== normalized.length
    ) {
      setSnack({ open: true, message: 'Los números de nota de crédito no pueden repetirse.', severity: 'error' });
      return false;
    }
    try {
      await Promise.all(incoming.map((number) => SettlementsAPI.checkCreditNote(number)));
      return true;
    } catch {
      setSnack({ open: true, message: 'Una de las notas de crédito ya existe.', severity: 'error' });
      return false;
    }
  };

  const handleSaveQuickCredit = async (note) => {
    const editIndex = Number(quickNoteDialog.editIndex);
    const valid = await validateQuickCreditNumbers(
      [note],
      editIndex >= 0 ? [editIndex] : []
    );
    if (!valid) return false;
    setCreditNotes((prev) => {
      const next = editIndex >= 0
        ? prev.map((item, index) => (index === editIndex ? note : item))
        : [...prev, note];
      return rebalanceQuickReconciliationNotes(next, invoices);
    });
    setQuickNoteDialog({ open: false, invoiceId: null, editIndex: -1 });
    return true;
  };

  const handleSaveBulkReconciliation = async (notes) => {
    const valid = await validateQuickCreditNumbers(notes);
    if (!valid) return false;
    setCreditNotes((prev) => rebalanceQuickReconciliationNotes(
      [...prev, ...notes],
      invoices
    ));
    setBulkReconciliationOpen(false);
    return true;
  };

  const handleAddCredit = async (cn) => {
    try {
      const num = String(cn?.number || '').trim();
      if (num) {
        await SettlementsAPI.checkCreditNote(num);
      }
    } catch (e) {
      setSnack({ open: true, message: 'Nota de credito duplicada', severity: 'error' });
      return;
    }

    if (cn?.linkedSuggestionKey) {
      const manualConflict = manualAdjustments.some((adj) => (
        Number(adj.invoiceId) === Number(cn.invoiceId)
        && String(adj.type) === String(cn.linkedType)
      ));
      if (manualConflict) {
        setSnack({
          open: true,
          message: 'Ya existe un ajuste manual para esa factura y concepto. Elimina ese ajuste antes de vincular una nota de credito.',
          severity: 'warning',
        });
        return;
      }

      const acceptedConflict = acceptedAutoAdjustments.some((adj) => (
        Number(adj.invoice_id) === Number(cn.invoiceId)
        && String(adj.type) === String(cn.linkedType)
      ));
      if (acceptedConflict) {
        if (String(cn.linkedSuggestionKey || '').startsWith('pronto_pago:')) {
          setEarlyPaymentStateByKey((prev) => ({
            ...prev,
            [cn.linkedSuggestionKey]: {
              ...(prev?.[cn.linkedSuggestionKey] || {}),
              enabled: false,
              toBill: false,
            },
          }));
        } else {
          setSuggestionSelections((prev) => ({
            ...prev,
            [cn.linkedSuggestionKey]: {
              ...(prev?.[cn.linkedSuggestionKey] || {}),
              enabled: false,
              toBill: false,
            },
          }));
        }
      }
    }

    const normalizedCredit = {
      ...cn,
      allocationAmount: creditAllocationAmount(cn),
      remainingCreditAmount: creditRemainingAmount(cn),
    };

    if (editCreditIdx >= 0) {
      setCreditNotes((arr) => rebalanceQuickReconciliationNotes(
        arr.map((item, i) => (i === editCreditIdx ? normalizedCredit : item)),
        invoices
      ));
      setEditCreditIdx(-1);
    } else {
      setCreditNotes((arr) => rebalanceQuickReconciliationNotes(
        [...arr, normalizedCredit],
        invoices
      ));
    }
    if (cn?.linkedSuggestionKey) {
      if (String(cn.linkedSuggestionKey || '').startsWith('pronto_pago:')) {
        setEarlyPaymentStateByKey((prev) => ({
          ...prev,
          [cn.linkedSuggestionKey]: {
            ...(prev?.[cn.linkedSuggestionKey] || {}),
            enabled: false,
            toBill: false,
          },
        }));
      } else {
        setSuggestionSelections((prev) => ({
          ...prev,
          [cn.linkedSuggestionKey]: {
            ...(prev?.[cn.linkedSuggestionKey] || {}),
            enabled: false,
            toBill: false,
          },
        }));
      }
    }
    setCreditNoteContext(null);
  };

  const handleRemoveCredit = (idx) => setCreditNotes((arr) => (
    rebalanceQuickReconciliationNotes(arr.filter((_, i) => i !== idx), invoices)
  ));

  const handleAddReceipt = (rc) => {
    if (editReceiptIdx >= 0) {
      setReceipts((arr) => arr.map((item, i) => (i === editReceiptIdx ? rc : item)));
      setEditReceiptIdx(-1);
    } else {
      setReceipts((arr) => [...arr, rc]);
    }
  };

  const handleRemoveReceipt = (idx) => setReceipts((arr) => arr.filter((_, i) => i !== idx));

  const handleSuggestionToggle = (key, checked) => {
    if (checked && linkedSuggestionNoteMap[key]) {
      setSnack({
        open: true,
        message: 'Esta diferencia ya tiene una nota de credito vinculada. Elimina o edita esa nota si deseas cambiar la forma de aplicacion.',
        severity: 'warning',
      });
      return;
    }
    if (String(key || '').startsWith('pronto_pago:')) {
      setEarlyPaymentStateByKey((prev) => ({
        ...prev,
        [key]: {
          ...(prev?.[key] || {}),
          enabled: Boolean(checked),
          toBill: false,
        },
      }));
      return;
    }
    setSuggestionSelections((prev) => ({
      ...prev,
      [key]: {
        ...(prev?.[key] || {}),
        enabled: Boolean(checked),
      },
    }));
  };

  const handleSuggestionBillToggle = (key, checked) => {
    if (checked && linkedSuggestionNoteMap[key]) {
      setSnack({
        open: true,
        message: 'La nota de credito vinculada ya sustituye esta diferencia. No puedes enviarla ademas a Bill.',
        severity: 'warning',
      });
      return;
    }
    if (String(key || '').startsWith('pronto_pago:')) {
      return;
    }
    setSuggestionSelections((prev) => ({
      ...prev,
      [key]: {
        ...(prev?.[key] || {}),
        enabled: checked ? true : Boolean(prev?.[key]?.enabled),
        toBill: Boolean(checked),
      },
    }));
  };

  const handleSuggestionAmountChange = (key, value) => {
    const raw = String(value ?? '').trim();
    const numeric = raw === '' ? '' : roundMoney(raw);
    if (String(key || '').startsWith('pronto_pago:')) {
      setEarlyPaymentStateByKey((prev) => ({
        ...prev,
        [key]: {
          ...(prev?.[key] || {}),
          amountEditable: numeric,
          amountManuallyEdited: raw !== '',
        },
      }));
      return;
    }
    setSuggestionSelections((prev) => ({
      ...prev,
      [key]: {
        ...(prev?.[key] || {}),
        amount: numeric,
      },
    }));
  };

  const handleOpenSuggestionCreditNote = (suggestion) => {
    if (!suggestion?.invoiceId || !suggestion?.key) return;

    const manualConflict = manualAdjustments.some((adj) => (
      Number(adj.invoiceId) === Number(suggestion.invoiceId)
      && String(adj.type) === String(suggestion.type)
    ));
    if (manualConflict) {
      setSnack({
        open: true,
        message: 'Ya existe un ajuste manual para esta factura y concepto. Eliminalo antes de vincular una nota de credito.',
        severity: 'warning',
      });
      return;
    }

    const existingIdx = creditNotes.findIndex((note) => note?.linkedSuggestionKey === suggestion.key);
    const existingNote = existingIdx >= 0 ? creditNotes[existingIdx] : null;
    const earlyState = earlyPaymentStateByKey?.[suggestion.key];
    const amount = roundMoney(
      (suggestion.type === 'pronto_pago'
        ? earlyState?.amountEditable
        : suggestionSelections?.[suggestion.key]?.amount)
      ?? existingNote?.amount
      ?? suggestion.amount
      ?? 0
    );
    const linkedInvoice = invoiceLookup[suggestion.invoiceId];
    const invoiceLabel = linkedInvoice
      ? `#${linkedInvoice.number}${linkedInvoice.series ? `  ${linkedInvoice.series}` : ''}`
      : `#${suggestion.invoiceId}`;

    if (suggestion.type === 'pronto_pago') {
      setEarlyPaymentStateByKey((prev) => ({
        ...prev,
        [suggestion.key]: {
          ...(prev?.[suggestion.key] || {}),
          enabled: false,
          toBill: false,
        },
      }));
    } else {
      setSuggestionSelections((prev) => ({
        ...prev,
        [suggestion.key]: {
          ...(prev?.[suggestion.key] || {}),
          enabled: false,
          toBill: false,
        },
      }));
    }

    setEditCreditIdx(existingIdx);
    setCreditNoteContext({
      mode: 'linked-suggestion',
      suggestionKey: suggestion.key,
      linkedType: suggestion.type,
      linkedSuggestionLabel: suggestion.label,
      linkedInvoiceLabel: invoiceLabel,
      invoiceId: suggestion.invoiceId,
      amount,
      reason: suggestion.reason || suggestion.label || '',
      forAdjustmentsOnly: true,
      linkedEffectiveDate: todayLocalDate(),
      linkedAdjustmentAmount: amount,
      linkedDetailId: suggestion.detailId || suggestion.detail_id || null,
      existingNote,
    });
    setOpenCreditNote(true);
  };

  const handleOpenEarlyPaymentMenu = (event, suggestionKey) => {
    setEarlyPaymentMenuState({
      anchorEl: event.currentTarget,
      suggestionKey: String(suggestionKey || ''),
    });
  };

  const handleCloseEarlyPaymentMenu = () => {
    setEarlyPaymentMenuState({ anchorEl: null, suggestionKey: '' });
  };

  const handleSelectEarlyPaymentRule = (suggestionKey, optionKey) => {
    const normalizedKey = String(suggestionKey || '').trim();
    const normalizedOptionKey = String(optionKey || '').trim();
    const base = earlyPaymentBaseByKey[normalizedKey];
    const options = base?.options || [];
    const selectedOption = options.find((opt) => opt.optionKey === normalizedOptionKey) || null;

    if (selectedOption && base) {
      const suggestedAmount = roundMoney(base.baseAmount * (getSafeNumber(selectedOption.percent) / 100));
      setEarlyPaymentStateByKey((prev) => ({
        ...prev,
        [normalizedKey]: {
          ...(prev?.[normalizedKey] || {}),
          selectedOptionKey: normalizedOptionKey,
          suggestedAmount,
          amountEditable: suggestedAmount,
          amountManuallyEdited: false,
        },
      }));
    }

    handleCloseEarlyPaymentMenu();
  };

  const handleManualDraftChange = (field, value) => {
    setManualAdjDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddManualAdjustment = () => {
    const amt = Number(manualAdjDraft.amount || 0);
    if (!manualAdjDraft.invoiceId || amt <= 0 || !manualAdjDraft.effectiveDate) {
      setSnack({ open: true, message: 'Completa factura, monto y fecha efectiva del ajuste', severity: 'warning' });
      return;
    }
    if (manualAdjDraft.effectiveDate > todayLocalDate()) {
      setSnack({ open: true, message: 'La fecha efectiva no puede ser futura', severity: 'warning' });
      return;
    }

    const suggestionConflict = acceptedAutoAdjustments.find((adj) => (
      Number(adj.invoice_id) === Number(manualAdjDraft.invoiceId)
      && String(adj.type) === String(manualAdjDraft.type)
    ));

    if (suggestionConflict) {
      setSnack({
        open: true,
        message: 'Ya existe una sugerencia aceptada para esta factura y concepto. Desmarcala o editala antes de agregar un ajuste manual.',
        severity: 'warning',
      });
      return;
    }

    const linkedCreditConflict = creditNotes.some((note) => (
      Number(note?.invoiceId) === Number(manualAdjDraft.invoiceId)
      && String(note?.linkedType || '') === String(manualAdjDraft.type)
      && Boolean(note?.linkedSuggestionKey)
    ));

    if (linkedCreditConflict) {
      setSnack({
        open: true,
        message: 'Ya existe una nota de credito vinculada para esta factura y concepto. No agregues ademas un ajuste manual.',
        severity: 'warning',
      });
      return;
    }

    const duplicateManual = manualAdjustments.some((adj) => (
      Number(adj.invoiceId) === Number(manualAdjDraft.invoiceId)
      && String(adj.type) === String(manualAdjDraft.type)
      && Boolean(adj.toBill) === Boolean(manualAdjDraft.toBill)
      && moneyEquals(adj.amount, amt)
      && normalizeTextValue(adj.reason) === normalizeTextValue(manualAdjDraft.reason)
    ));

    if (duplicateManual) {
      setSnack({
        open: true,
        message: 'Ese ajuste manual ya fue agregado.',
        severity: 'warning',
      });
      return;
    }

    setManualAdjustments((prev) => [
      ...prev,
      {
        ...manualAdjDraft,
        amount: amt,
        groupKey: `manual:${manualAdjDraft.invoiceId}:${manualAdjDraft.type}:${Date.now()}`,
      },
    ]);
    setManualAdjDraft({
      invoiceId: manualAdjDraft.invoiceId,
      type: 'shortage',
      amount: '',
      reason: '',
      toBill: false,
      effectiveDate: todayLocalDate(),
    });
  };

  const handleRemoveManualAdjustment = (idx) => {
    setManualAdjustments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBillUsageChange = (billId, value, available) => {
    const num = Number(value) || 0;
    const safe = Math.max(0, Math.min(num, Number(available || 0)));
    setBillUsages((prev) => ({ ...prev, [billId]: safe }));
  };

  const uploadPaymentDocumentFiles = useCallback(async (paymentId, files = [], knownDocumentId = null) => {
    const hasFileCtor = typeof File !== 'undefined';
    const validFiles = (files || []).filter((file) => !hasFileCtor || file instanceof File);
    if (!paymentId || validFiles.length === 0) {
      throw new Error('No hay un comprobante válido para cargar.');
    }

    let documentId = knownDocumentId;
    if (!documentId) {
      const { data: paymentDetail } = await SettlementsAPI.paymentDetail(paymentId);
      documentId = paymentDetail?.document?.id;
    }
    if (!documentId) {
      throw new Error('El pago fue creado, pero no se encontró su documento.');
    }

    const [primaryFile, ...extraFiles] = validFiles;
    if (primaryFile) {
      const fd = new FormData();
      fd.append('paymentFile', primaryFile);
      await API.patch(`payment/payment-documents/${documentId}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    const extraResults = [];
    for (const attachment of extraFiles) {
      const afd = new FormData();
      afd.append('payment_document', String(documentId));
      afd.append('file', attachment);
      try {
        await API.post('payment/payment-document-attachments/', afd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        extraResults.push(true);
      } catch {
        extraResults.push(false);
      }
    }
    return {
      primaryUploaded: true,
      extraFailures: extraResults.filter((uploaded) => !uploaded).length,
    };
  }, []);

  const retryPendingAchUpload = useCallback(async () => {
    if (!pendingAchUploads.length || retryingAchUploads) return;
    setRetryingAchUploads(true);
    const results = await Promise.allSettled(
      pendingAchUploads.map((entry) => uploadPaymentDocumentFiles(
        entry.paymentId,
        entry.files,
        entry.documentId
      ))
    );
    const remaining = pendingAchUploads.filter(
      (_, index) => results[index]?.status === 'rejected'
    );
    if (remaining.length) {
      setPendingAchUploads(remaining);
      setSnack({
        open: true,
        message: 'El pago ya fue creado, pero el comprobante ACH continúa pendiente. Puedes reintentar desde aquí o desde el detalle del pago.',
        severity: 'error',
      });
    } else {
      setPendingAchUploads([]);
      setSuccessInfo(pendingSettlementResult?.successInfo || {
        type: 'success',
        data: pendingSettlementResult?.data,
      });
      setPendingSettlementResult(null);
      setSnack({
        open: true,
        message: 'Comprobante ACH cargado correctamente.',
        severity: 'success',
      });
      setActiveStep(3);
    }
    setRetryingAchUploads(false);
  }, [
    pendingAchUploads,
    pendingSettlementResult,
    retryingAchUploads,
    uploadPaymentDocumentFiles,
  ]);

  const handleFinalize = async (excessDecision = null, options = {}) => {
    try {
      if (pendingAchUploads.length) {
        setSnack({
          open: true,
          message: 'El pago ya fue creado. Reintenta el comprobante ACH pendiente; no vuelvas a liquidarlo.',
          severity: 'warning',
        });
        return;
      }
      if (blockProceed) {
        setSnack({ open: true, message: 'No hay saldo pagable para las facturas seleccionadas.', severity: 'warning' });
        return;
      }
      if (hasCompensationOnly) {
        if (payDocs.length > 0) {
          setSnack({ open: true, message: 'No se permiten pagos en efectivo para la compensacion.', severity: 'warning' });
          return;
        }
        if (creditNotes.length === 0 && billCreditsTotal <= 0) {
          if (reconciliationOnly) {
            setSnack({
              open: true,
              message: 'Captura las notas de crédito de las facturas pendientes antes de finalizar.',
              severity: 'warning',
            });
            return;
          }
          const { data } = await SettlementsAPI.compensateAdjustments({
            supplier_id: supplierId,
            invoice_ids: selectedInvoices.map(r => r.id),
          });
          await fetchInvoices();
          setSuccessInfo({ type: 'compensation', data });
          setSnack({ open: false, message: '', severity: 'info' });
          setActiveStep(3);
          return;
        }
      }

      const achWithoutFile = payDocs.find(
        (payment) => normalizePaymentMethod(payment.type, payment.isAch) === 'ach'
          && !(payment.files || []).length
      );
      if (achWithoutFile) {
        setSnack({
          open: true,
          message: 'La transferencia ACH requiere al menos un comprobante.',
          severity: 'error',
        });
        return;
      }

      const paymentsPayload = payDocs.map(p => {
        const rawAmount = Number(p.amount || 0);
        return {
          company_id: company?.id || null,
          method: normalizePaymentMethod(p.type, p.isAch),
          amount: String(Number(rawAmount || 0).toFixed(2)),
          payment_date: p.date,
          comment: p.comment || '',
          has_receipt: receipts.length > 0,
          document: p?.bankAccount ? {
            number: p.reference || p.number || '',
            total: String(Number(rawAmount || 0).toFixed(2)),
            bank_account_id: p.bankAccount?.id || p.bankAccountId || null,
          } : null,
          receipts: receipts.length ? receipts.map(r => ({
            number: r.number || '',
            date: r.date,
            amount: String(Number(r.amount || 0).toFixed(2)),
            receipt_type: r.rType || r.type || 'electronico',
            is_physical: Boolean(r.is_ph),
          })) : [],
        };
      });

      if (!company?.id) {
        setSnack({ open: true, message: 'Selecciona una empresa activa', severity: 'error' });
        return;
      }

      const payload = {
        supplier_id: supplierId,
        company_id: company?.id || null,
        invoice_ids: selectedInvoices.map(r => r.id),
        credit_only_has_receipt: (payDocs.length === 0 && creditNotes.length > 0 && creditOnlyHasReceipt) ? true : false,
        comment: settlementComment || '',
        excess_action: excessDecision?.action || 'ADVANCE',
        excess_invoice_id: excessDecision?.action === 'UNJUSTIFIED'
          ? Number(excessDecision.invoiceId || 0) || null
          : null,
        minor_difference_confirmed: Boolean(options.minorDifferenceConfirmed),
        payments: paymentsPayload,
        credit_notes: creditNotes.map(c => ({
          number: c.number || '',
          date: c.date,
          reason: c.reason || '',
          amount: String(Number(c.amount || 0).toFixed(2)),
          invoice_id: Number(c.invoiceId || 0),
          application_mode: c.applicationMode
            || (c.forAdjustmentsOnly || c.linkedSuggestionKey ? 'reconciliation' : 'direct'),
          for_adjustments_only: Boolean(c.forAdjustmentsOnly),
          linked_group_key: c.linkedSuggestionKey || '',
          linked_type: c.linkedType || '',
          linked_source: c.linkedSource || '',
          linked_effective_date: c.linkedEffectiveDate || null,
          linked_adjustment_amount: c.linkedAdjustmentAmount != null
            ? String(Number(c.linkedAdjustmentAmount || 0).toFixed(2))
            : null,
          linked_detail_id: c.linkedDetailId || null,
        })),
        bill_usages: Object.entries(billUsages || {})
          .filter(([, amt]) => Number(amt) > 0)
          .map(([billId, amt]) => ({
            bill_id: Number(billId),
            amount: String(Number(amt || 0).toFixed(2)),
          })),
        allocations: {
          payments: Object.entries(allocations || {}).flatMap(([invoiceId, amt]) => {
            let remaining = roundMoney(amt || 0);
            const rows = [];
            payDocs.forEach((p, idx) => {
              if (remaining <= 0) return;
              const take = roundMoney(Math.min(remaining, Number(p.amount || 0)));
              if (take > 0) {
                rows.push({ payment_index: idx, invoice_id: Number(invoiceId), amount: String(take.toFixed(2)) });
                remaining = roundMoney(remaining - take);
              }
            });
            return rows;
          }),
          credits: creditNotes.map((c, idx) => ({
            credit_index: idx,
            invoice_id: Number(c.invoiceId),
            amount: String(creditAllocationAmount(c).toFixed(2)),
          })),
        },
        adjustments: adjustmentsPayload.map(a => ({
          invoice_id: a.invoice_id,
          detail_id: a.detail_id || null,
          type: a.type,
          amount: String(Number(a.amount || 0).toFixed(2)),
          effective_date: a.effective_date || todayLocalDate(),
          reason: a.reason || '',
          to_bill: Boolean(a.to_bill),
          source: a.source || 'manual',
          group_key: a.group_key || '',
        })),
      };

      const { data: preview } = await SettlementsAPI.preview(payload);
      setSettlementPreview(preview);
      if (preview?.requires_distribution && !preview?.distribution_complete) {
        setSnack({
          open: true,
          message: 'Distribuye el pago según la decisión indicada por el cálculo contable.',
          severity: 'warning',
        });
        return;
      }
      if (
        preview?.minor_difference_required
        && !options.minorDifferenceConfirmed
      ) {
        setMinorDifferenceDialog({
          open: true,
          amount: Number(preview.minor_difference_amount || 0),
          excessDecision,
        });
        return;
      }
      if (Number(preview?.excess_amount || 0) > 0 && !excessDecision) {
        setExcessDialog({
          open: true,
          totalPayable: Number(preview.required_payment_total || 0),
          payment: Number(preview.payment_total || 0),
          excess: Number(preview.excess_amount || 0),
          action: 'ADVANCE',
          invoiceId: selectedInvoices.length === 1 ? String(selectedInvoices[0].id) : '',
        });
        return;
      }

      const { data } = await SettlementsAPI.settle(payload);
      if (supplierId) {
        try {
          await fetchBills();
        } catch (billErr) {
          console.warn('No se pudo refrescar saldos a favor despues de liquidar', billErr);
        }
      }
      let hasUploadWarning = false;
      const uploadJobs = (data?.payments_created || [])
        .map((created, idx) => ({
          paymentId: created?.id,
          documentId: created?.document_id,
          method: normalizePaymentMethod(
            created?.method || payDocs[idx]?.type,
            payDocs[idx]?.isAch
          ),
          files: payDocs[idx]?.files || [],
        }))
        .filter((entry) => entry.paymentId && Array.isArray(entry.files) && entry.files.length > 0);
      let uploadResults = [];
      if (uploadJobs.length > 0) {
        uploadResults = await Promise.allSettled(
          uploadJobs.map((entry) => uploadPaymentDocumentFiles(
            entry.paymentId,
            entry.files,
            entry.documentId
          ))
        );
        hasUploadWarning = uploadResults.some(
          (result) => result.status === 'rejected'
            || Number(result.value?.extraFailures || 0) > 0
        );
      }
      console.log('Liquidacion creada', data);
      const hadReceipt = cashOnlyPayments || receipts.length > 0 || (payDocs.length === 0 && creditOnlyHasReceipt);
      const nonPhysical = hadReceipt && receipts.some(r => !r.is_ph);
      const nextSuccessInfo = {
        type: hadReceipt ? (nonPhysical ? 'warning-non-physical' : 'success') : 'warning-no-receipt',
        data,
      };
      const pendingAch = uploadJobs.filter(
        (entry, index) => entry.method === 'ach' && uploadResults[index]?.status === 'rejected'
      );
      if (pendingAch.length) {
        setPendingAchUploads(pendingAch);
        setPendingSettlementResult({
          data,
          successInfo: nextSuccessInfo,
        });
        setSuccessInfo(null);
        setSnack({
          open: true,
          message: `El pago ACH ${pendingAch.map((entry) => `#${entry.paymentId}`).join(', ')} fue creado, pero su comprobante está pendiente. Reintenta la carga antes de finalizar.`,
          severity: 'error',
        });
        return;
      }

      setSuccessInfo(nextSuccessInfo);
      if (hasUploadWarning) {
        setSnack({ open: true, message: 'El pago se registro, pero algunos adjuntos no se pudieron guardar.', severity: 'warning' });
      } else if (data?.excess_bill?.id) {
        setSnack({
          open: true,
          message: `Se cre un saldo a favor #${data.excess_bill.id} por ${formatCurrency(data.excess_bill.amount)}.`,
          severity: 'success',
        });
      } else if ((data?.excess_adjustments || []).length > 0) {
        const unjustifiedTotal = sumMoney(
          data.excess_adjustments.map((item) => Number(item?.amount || 0))
        );
        setSnack({
          open: true,
          message: `Se registro ${formatCurrency(unjustifiedTotal)} como pago sin justificacion, sin afectar el saldo.`,
          severity: 'success',
        });
      } else {
        setSnack({ open: false, message: '', severity: 'info' });
      }
      setActiveStep(3);
    } catch (e) {
      console.error('Error al liquidar', e);
      setSnack({
        open: true,
        message: extractApiError(e, 'No se pudo completar la liquidacion.'),
        severity: 'error',
      });
    }
  };

  const handleConfirmExcess = () => {
    if (excessDialog.action === 'UNJUSTIFIED' && !excessDialog.invoiceId) {
      setSnack({
        open: true,
        message: 'Selecciona la factura relacionada con el pago sin justificacion.',
        severity: 'warning',
      });
      return;
    }
    const decision = {
      action: excessDialog.action,
      invoiceId: excessDialog.invoiceId,
    };
    setExcessDialog((prev) => ({ ...prev, open: false }));
    handleFinalize(decision);
  };

  const handleConfirmMinorDifference = () => {
    const decision = minorDifferenceDialog.excessDecision;
    setMinorDifferenceDialog((prev) => ({ ...prev, open: false }));
    handleFinalize(decision, { minorDifferenceConfirmed: true });
  };

  const SupplierCard = ({ supplierName, supplierId, bankAccounts, onReset }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderRadius: 1.5,
        backgroundColor: supplierName ? 'primary.lighter' : 'background.paper',
        border: '1px solid',
        borderColor: supplierName ? 'primary.light' : 'divider',
        transition: 'all 0.2s ease',
        maxWidth: 'fit-content'
      }}
    >
      {/* Avatar minimalista */}
      {supplierName && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          {String(supplierName)[0]?.toUpperCase()}
        </Avatar>
      )}

      {/* Informacin principal */}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {supplierName || "Sin proveedor"}
        </Typography>
      </Box>

      {/* Botn de reinicio */}
      {supplierName && (
        <IconButton
          size="small"
          onClick={onReset}
          sx={{
            width: 28,
            height: 28,
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          <RefreshButtonSpinner onClick={resetStepper} tittle='Restablecer proveedor' />
        </IconButton>
      )}
    </Box>
  );
  // JSX del componente (igual al original)
  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 0.5,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stepper nonLinear activeStep={activeStep} sx={{ width: 'max-content', minWidth: '100%', px: 0.5, '& .MuiStep-root': { flexShrink: 0 }, '& .MuiStepConnector-line': { minHeight: 2 }, }}>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepButton color="inherit" disabled={index === activeStep}>
                {index < activeStep ? (
                  <Typography variant='subtitle2'>Completado</Typography>
                ) : (
                  label
                )}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ mt: 1.5, mb: 2 }}>
        <PaymentTotalsBar
          invoiceCount={selectedInvoices.length}
          totalInvoicesLabel={formatCurrency(totalInvoices)}
          adjustmentsLabel={formatCurrency(adjustmentsTotal)}
          creditsLabel={formatCurrency(roundMoney(totalCredits + totalPreviousPayments))}
          billCreditsLabel={formatCurrency(billCreditsTotal)}
          effectiveDueLabel={formatCurrency(effectiveDue)}
          paymentsLabel={formatCurrency(totalPayments)}
          remainingLabel={formatCurrency(remaining)}
          remainingIsCredit={remaining < 0}
        />
      </Box>
      {hasCompensationOnly && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2, py: 0.5, '& .MuiAlert-message': { py: 0 } }}
        >
          <Typography variant="caption">
            Esta seleccion se liquida unicamente con Nota de Credito (no requiere pago).
          </Typography>
        </Alert>
      )}

      {activeStep === 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <CustomAutoComplete
              key={`sup-${supplierId}`}
              name={'supplier_id'}
              onChange={handleSupplierChange}
              supplier={supplierId}
              setSupplierName={setSupplierName}
            />
            {supplierName && (
              <SupplierCard
                supplierName={supplierName}
                supplierId={supplierId}
                bankAccounts={bankAccounts}
                onReset={resetStepper}
              />
            )}

            {supplierId ? (
              <FormControl size="small" sx={{ minWidth: 230 }}>
                <Select
                  value={invoiceScope}
                  onChange={(e) => setInvoiceScope(String(e.target.value || 'payable'))}
                  displayEmpty
                  sx={{ fontSize: '0.82rem', height: 40 }}
                >
                  <MenuItem value="payable">Facturas realmente por pagar</MenuItem>
                  <MenuItem value="compensate_only">Solo por compensar (NC/ajustes)</MenuItem>
                  <MenuItem value="all">Todas las elegibles</MenuItem>
                </Select>
              </FormControl>
            ) : null}


            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              {/* Campo de busqueda integrado */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: showInvoiceSearch ? 240 : 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: showInvoiceSearch ? 'background.paper' : 'transparent',
                  border: '1px solid',
                  borderColor: showInvoiceSearch ? 'divider' : 'transparent',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  boxShadow: showInvoiceSearch ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {/* Icono de busqueda siempre visible */}
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!showInvoiceSearch) {
                      setShowInvoiceSearch(true);
                    }
                  }}
                  sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    color: showInvoiceSearch ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>

                {/* Campo de texto que se expande */}
                {showInvoiceSearch && (
                  <Box
                    sx={{
                      flex: 1,
                      animation: 'expand 0.3s ease',
                      '@keyframes expand': {
                        '0%': { opacity: 0, width: 0 },
                        '100%': { opacity: 1, width: '100%' }
                      }
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Buscar facturas..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '0.875rem',
                        padding: '8px 0',
                        color: 'inherit',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Box>
                )}

                {/* Botn para cerrar/cancelar */}
                {showInvoiceSearch && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowInvoiceSearch(false);
                      setInvoiceSearch('');
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 0.5,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Stack>

          {/* El resto del cdigo permanece igual */}
          <Box
            sx={{
              height: 380,
              width: '100%',
              mt: 1,

              // headers
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
              },

              // celdas: aire horizontal + vertical leve
              '& .MuiDataGrid-cell': {
                px: 1,          // <-- aire horizontal (clave)
                py: 0.5,
                alignItems: 'center',
              },

              // filas un poquito mas separadas visualmente
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },

              // hover (ver punto 3)
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'action.hover',
              },

              // seleccionado (para que se note)
              '& .MuiDataGrid-row.Mui-selected': {
                bgcolor: 'action.selected',
              },
              '& .MuiDataGrid-row.Mui-selected:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >

            <DataGrid
              rows={invoices}
              rowHeight={60}
              columns={columns}
              loading={loading}
              filterModel={invoiceFilterModel}
              pagination
              paginationModel={invoicePaginationModel}
              onPaginationModelChange={setInvoicePaginationModel}
              checkboxSelection
              disableColumnFilter
              disableColumnMenu
              keepNonExistentRowsSelected
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(m) => setSelectionModel(m)}
              onRowClick={(params) => {
                const id = params.id;
                setSelectionModel((prev) => (
                  prev.includes(id)
                    ? prev.filter((x) => x !== id)
                    : [...prev, id]
                ));
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              // Hacer la tabla mas compacta
              density="compact"
              // Mejorar la apariencia general
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                }, '& .MuiDataGrid-cell': {
                  py: 0,          // evita que crezca la fila sin avisar
                  px: 1.25,       // aire horizontal real
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>

          {invoiceHasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MoreHoriz />}
                onClick={() => fetchInvoices({ append: true, offsetOverride: invoiceServerOffset })}
                disabled={loading}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Cargar mas facturas
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              variant='outlined'
              color="inherit"
              size='small'
              disabled
              startIcon={<ArrowBackIosNew />}
              sx={{ mr: 1, ml: 0, textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }}
            >
              Datos borrados
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              onClick={() => { lastSelectionRef.current = selectionModel; setActiveStep(1); }}
              size='small'
              variant='contained'
              color='primary'
              sx={{ mr: 1, textTransform: 'none', borderRadius: 2, px: 2.5, fontSize: '0.85rem' }}
              disabled={selectionModel.length === 0 || blockProceed}
              endIcon={<ArrowForwardIos />}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 1 && (
        reconciliationOnly ? (
          <QuickReconciliationPanel
            invoices={selectedInvoices}
            notes={creditNotes}
            onAdd={(invoice) => openQuickReconciliationNote(invoice)}
            onManage={(invoice) => openQuickReconciliationManager(invoice)}
            onOpenBulk={() => setBulkReconciliationOpen(true)}
            onBack={() => {
              if (invoices.length === 0 && supplierId) fetchInvoices();
              setSelectionModel((prev) => (prev?.length ? prev : (lastSelectionRef.current || [])));
              setActiveStep(0);
            }}
            onContinue={() => handleFinalize()}
            continueLabel="Finalizar conciliación"
          />
        ) : (
          <PaymentSummaryStep
          selectedInvoices={selectedInvoices}
          supplierName={supplierName}
          selectedDetails={selectedDetails}
          selectedBonusDetails={selectedBonusDetails}
          detailsByInvoice={detailsByInvoice}
          shortageSuggestions={shortageSuggestions}
          priceDiffSuggestions={priceDiffSuggestions}
          bonusSuggestions={bonusSuggestions}
          earlyPaymentSuggestions={earlyPaymentSuggestions}
          adjustmentsPayload={adjustmentsPayload}
          creditNotes={creditNotes}
          payableByInvoice={payableByInvoice}
          formatCurrency={formatCurrency}
          summaryUiState={summaryUiState}
          onSummaryUiStateChange={setSummaryUiState}
          onBack={() => {
            if (invoices.length === 0 && supplierId) {
              fetchInvoices();
            }
            setSelectionModel((prev) => (prev && prev.length ? prev : (lastSelectionRef.current || [])));
            setActiveStep(0);
          }}
          onContinue={() => setActiveStep(2)}
          disableContinue={selectedInvoices.length === 0 || blockProceed}
          />
        )
      )}
      {activeStep === 2 && (
        <Box sx={{ mt: 2 }}>
          {pendingAchUploads.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              action={(
                <Button
                  color="inherit"
                  size="small"
                  onClick={retryPendingAchUpload}
                  disabled={retryingAchUploads}
                >
                  {retryingAchUploads ? 'Cargando...' : 'Reintentar carga'}
                </Button>
              )}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Comprobante pendiente
              </Typography>
              <Typography variant="caption">
                El pago ACH {pendingAchUploads.map((entry) => `#${entry.paymentId}`).join(', ')} ya fue creado.
                No se volverá a liquidar; carga el comprobante para finalizar. También puedes reintentarlo desde el detalle del pago.
              </Typography>
            </Alert>
          )}
          <PaymentDetailStep
            showNotCompleted={showNotCompleted}
            showCompleteNeutral={showCompleteNeutral}
            hasCompensationOnly={hasCompensationOnly}
            totalPayments={totalPayments}
            hasNonCashPayments={hasNonCashPayments}
            movements={movements}
            onOpenPaymentDocument={() => { setEditPayIdx(-1); setOpenPayDoc(true); }}
            onOpenCreditNote={() => { setEditCreditIdx(-1); setCreditNoteContext(null); setOpenCreditNote(true); }}
            onOpenReceipt={() => { setEditReceiptIdx(-1); setOpenReceipt(true); }}
            onEditMovement={(movement) => movement?.onEdit?.()}
            onDeleteMovement={(movement) => movement?.onDelete?.()}
            shortageSuggestions={shortageSuggestions}
            optionalAdjustmentSuggestions={optionalAdjustmentSuggestions}
            suggestionSelections={suggestionSelections}
            earlyPaymentStateByKey={earlyPaymentStateByKey}
            earlyPaymentBaseByKey={earlyPaymentBaseByKey}
            linkedSuggestionNoteMap={linkedSuggestionNoteMap}
            onSuggestionToggle={handleSuggestionToggle}
            onSuggestionBillToggle={handleSuggestionBillToggle}
            onSuggestionAmountChange={handleSuggestionAmountChange}
            onOpenSuggestionCreditNote={handleOpenSuggestionCreditNote}
            onOpenEarlyPaymentMenu={handleOpenEarlyPaymentMenu}
            manualAdjDraft={manualAdjDraft}
            onManualDraftChange={handleManualDraftChange}
            onAddManualAdjustment={handleAddManualAdjustment}
            manualAdjustments={manualAdjustments}
            onRemoveManualAdjustment={handleRemoveManualAdjustment}
            selectedInvoices={selectedInvoices}
            selectableBills={selectableBills}
            loadingBills={loadingBills}
            billUsages={billUsages}
            onBillUsageChange={handleBillUsageChange}
            billCreditsTotal={billCreditsTotal}
            settlementComment={settlementComment}
            onSettlementCommentChange={setSettlementComment}
            canDistribute={canDistribute2}
            distributionComplete={distributionComplete2}
            onOpenAllocate={() => setOpenAllocate(true)}
            onBack={() => setActiveStep(1)}
            onFinalize={handleFinalize}
            finalizeGuard={
              pendingAchUploads.length
                ? { ok: false, reason: 'El pago ya fue creado y tiene un comprobante ACH pendiente.' }
                : finalizeGuard2
            }
            payDocs={payDocs}
            creditNotes={creditNotes}
            creditOnlyHasReceipt={creditOnlyHasReceipt}
            onCreditOnlyHasReceiptChange={setCreditOnlyHasReceipt}
            formatCurrency={formatCurrency}
          />

          <CustomModal
            open={openPayDoc}
            setOpen={setOpenPayDoc}
            handleClose={() => setOpenPayDoc(false)}
            title={"Agregar medio de pago"}
            component={PaymentDocumentForm}
            icon={AddCard}
            width={"30%"}
            componentProps={{ onSave: handleAddPayDoc, supplierBankAccounts: bankAccounts, initialData: editPayIdx >= 0 ? payDocs[editPayIdx] : null }}
          />
          <CustomModal
            open={openCreditNote}
            setOpen={setOpenCreditNote}
            handleClose={() => { setOpenCreditNote(false); setCreditNoteContext(null); }}
            title={creditNoteContext?.mode === 'linked-suggestion' ? 'Vincular nota de credito' : 'Agregar nota de credito'}
            component={CreditNoteForm}
            icon={Add}
            width={"30%"}
            componentProps={{
              onSave: handleAddCredit,
              initialData: editCreditIdx >= 0 ? creditNotes[editCreditIdx] : null,
              invoices: selectedInvoices,
              supplierId,
              hideReason: creditNoteContext?.mode === 'linked-suggestion',
              hideInvoiceSelect: creditNoteContext?.mode === 'linked-suggestion',
              lockedInvoiceId: creditNoteContext?.invoiceId || null,
              lockedInvoiceLabel: creditNoteContext?.linkedInvoiceLabel || '',
              linkedSuggestionLabel: creditNoteContext?.linkedSuggestionLabel || '',
              linkedSuggestionKey: creditNoteContext?.suggestionKey || '',
              linkedType: creditNoteContext?.linkedType || '',
              linkedSource: creditNoteContext?.mode === 'linked-suggestion' ? 'suggestion' : '',
              linkedEffectiveDate: creditNoteContext?.linkedEffectiveDate || null,
              linkedAdjustmentAmount: creditNoteContext?.linkedAdjustmentAmount ?? null,
              linkedDetailId: creditNoteContext?.linkedDetailId || null,
              presetReason: creditNoteContext?.reason || '',
              presetAmount: creditNoteContext?.amount ?? null,
              forAdjustmentsOnly: Boolean(creditNoteContext?.forAdjustmentsOnly),
            }}
          />
          <CustomModal
            open={openReceipt}
            setOpen={setOpenReceipt}
            handleClose={() => setOpenReceipt(false)}
            title={"Agregar recibo de caja"}
            component={ReceiptForm}
            icon={Add}
            width={"30%"}
            componentProps={{ onSave: handleAddReceipt, initialData: editReceiptIdx >= 0 ? receipts[editReceiptIdx] : null }}
          />
          <CustomModal
            open={openAllocate}
            setOpen={setOpenAllocate}
            handleClose={() => setOpenAllocate(false)}
            title={'Distribuir pago parcial'}
            component={AllocationContent}
            icon={Add}
            width={'40%'}
            componentProps={{
              onSave: (map) => { setAllocations(map || {}); setOpenAllocate(false); },
              invoices: selectedInvoices.map((inv) => ({
                ...inv,
                netDue: Number(
                  settlementPreview?.payment_need_by_invoice?.[String(inv.id)]
                  ?? payableByInvoice[inv.id]
                  ?? inv.totalNum
                ),
                due: Number(
                  settlementPreview?.payment_need_by_invoice?.[String(inv.id)]
                  ?? payableByInvoice[inv.id]
                  ?? inv.totalNum
                ),
              })),
              amountAvailable: Number(settlementPreview?.payment_total ?? totalPayments),
              initial: allocations,
            }}
          />
        </Box>
      )}
      <QuickReconciliationNoteDialog
        open={quickNoteDialog.open}
        invoice={quickDialogInvoice}
        initialNote={quickDialogInitialNote}
        availableAmount={quickDialogAvailable}
        onClose={() => setQuickNoteDialog({ open: false, invoiceId: null, editIndex: -1 })}
        onSave={handleSaveQuickCredit}
      />
      <QuickReconciliationManagerDialog
        open={Boolean(quickManagerInvoice)}
        invoice={quickManagerInvoice}
        notes={quickManagerNotes}
        state={quickManagerState}
        onClose={() => setQuickManagerInvoiceId(null)}
        onAdd={() => {
          const invoice = quickManagerInvoice;
          setQuickManagerInvoiceId(null);
          openQuickReconciliationNote(invoice);
        }}
        onEdit={(index) => {
          const invoice = quickManagerInvoice;
          setQuickManagerInvoiceId(null);
          openQuickReconciliationNote(invoice, index);
        }}
        onDelete={removeQuickReconciliationNote}
      />
      <BulkReconciliationNotesDialog
        open={bulkReconciliationOpen}
        invoices={selectedInvoices}
        notes={creditNotes}
        onClose={() => setBulkReconciliationOpen(false)}
        onSave={handleSaveBulkReconciliation}
      />
      <Menu
        anchorEl={earlyPaymentMenuState.anchorEl}
        open={Boolean(earlyPaymentMenuState.anchorEl)}
        onClose={handleCloseEarlyPaymentMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {((earlyPaymentBaseByKey[earlyPaymentMenuState.suggestionKey]?.options) || []).map((option) => {
          const selected = String(
            earlyPaymentStateByKey?.[earlyPaymentMenuState.suggestionKey]?.selectedOptionKey || ''
          ) === option.optionKey;
          return (
            <MenuItem
              key={`${earlyPaymentMenuState.suggestionKey}:${option.optionKey}`}
              selected={selected}
              onClick={() => handleSelectEarlyPaymentRule(earlyPaymentMenuState.suggestionKey, option.optionKey)}
              sx={{ minWidth: 240 }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                  {option.percent}%  {option.period} dia(s)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                  Base {option.baseType}  restan {option.dueIn} dia(s)
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
      <Dialog
        open={minorDifferenceDialog.open}
        onClose={() => setMinorDifferenceDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Diferencia menor de liquidación</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            Queda una diferencia de {formatCurrency(minorDifferenceDialog.amount)}.
            {' '}¿Deseas registrarla como ajuste por diferencia menor?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMinorDifferenceDialog((prev) => ({ ...prev, open: false }))}
          >
            Volver y corregir
          </Button>
          <Button variant="contained" onClick={handleConfirmMinorDifference}>
            Registrar diferencia
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={excessDialog.open}
        onClose={() => setExcessDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resolver excedente de pago</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Total pagable</Typography>
                <Typography fontWeight={700}>{formatCurrency(excessDialog.totalPayable)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pago</Typography>
                <Typography fontWeight={700}>{formatCurrency(excessDialog.payment)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Excedente</Typography>
                <Typography fontWeight={700} color="warning.main">{formatCurrency(excessDialog.excess)}</Typography>
              </Box>
            </Box>

            <RadioGroup
              value={excessDialog.action}
              onChange={(event) => setExcessDialog((prev) => ({
                ...prev,
                action: event.target.value,
              }))}
            >
              <FormControlLabel
                value="ADVANCE"
                control={<Radio />}
                label="Crear adelanto (saldo a favor para futuras liquidaciones)"
              />
              <FormControlLabel
                value="UNJUSTIFIED"
                control={<Radio />}
                label="Pago sin justificacion (solo registro, no afecta el saldo)"
              />
            </RadioGroup>

            {excessDialog.action === 'UNJUSTIFIED' && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel id="excess-invoice-label">Factura relacionada</InputLabel>
                  <Select
                    labelId="excess-invoice-label"
                    label="Factura relacionada"
                    value={excessDialog.invoiceId}
                    onChange={(event) => setExcessDialog((prev) => ({
                      ...prev,
                      invoiceId: event.target.value,
                    }))}
                  >
                    {selectedInvoices.map((invoice) => (
                      <MenuItem key={invoice.id} value={String(invoice.id)}>
                        #{invoice.number}{invoice.series ? ` · ${invoice.series}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="info">
                  El excedente quedara vinculado al pago como un ajuste informativo RECORD_ONLY.
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcessDialog((prev) => ({ ...prev, open: false }))}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirmExcess}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
      {activeStep === 3 && (
        <SuccessMessage
          success={successInfo?.type === 'success'
            ? 'Pago registrado con recibo'
            : successInfo?.type === 'compensation'
              ? 'Compensacion registrada'
              : ''}
          alert={
            successInfo?.type === 'warning-non-physical'
              ? 'Recibo no fisico'
              : successInfo?.type === 'warning-no-receipt'
                ? 'Pago sin recibo'
                : ''
          }
          buttonText="Hacer otro pago"
          title={
            successInfo?.type === 'success'
              ? 'Pago registrado con recibo'
              : successInfo?.type === 'compensation'
                ? 'Compensacion registrada'
              : successInfo?.type === 'warning-non-physical'
                ? 'Recibo no fisico'
                : 'Pago sin recibo'
          }
          subtitle={
            successInfo?.type === 'success'
              ? 'La liquidacion se completo correctamente.'
              : successInfo?.type === 'compensation'
                ? 'Las facturas se compensaron con ajustes pendientes.'
              : successInfo?.type === 'warning-non-physical'
                ? 'El pago se registro, pero el recibo no es fisico.'
                : 'Pago realizado con exito, sin recibo.'
          }
          onNewInvoice={resetStepperLight}
        />
      )}
      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}


